import { useState, useEffect, useCallback, useRef, useMemo, memo } from "react";
import { useParams, useNavigate, useSearchParams, useLocation, Routes, Route, NavLink, Navigate } from "react-router-dom";
import {
  getPortalBranding, portalLogin,
  getPortalTickets, getPortalTicket, createPortalTicket, addPortalResponse, uploadPortalAttachments,
  getPortalTicketCounts, getPortalActivity, resolvePortalTicket,
  getPortalDevices, getPortalKBCategories, getPortalKBArticles, getPortalKBArticle,
  getPortalDesktopStatus, getPortalSecureLink, getSecureLinkMeta,
  PORTAL_EXPIRED_EVENT, portalTokenSecondsLeft, PORTAL_API,
} from "../api";
import {
  Ticket, Monitor, LayoutDashboard, LogOut, Plus, ChevronRight, Newspaper,
  AlertCircle, CheckCircle, Clock, Send, X, Menu, RefreshCw,
  BookOpen, Search, ArrowLeft, Globe, Lock, Eye, Paperclip,
  Download, Apple, MonitorDown,
  MessageSquare, Laptop, ShieldCheck, ArrowRight, Users, User, Inbox, Wrench, CheckCircle2, LifeBuoy,
  AlertTriangle, ExternalLink,
} from "lucide-react";
import { linkLocalDevice } from "./linkLocalDevice";
import { APP_VERSION } from "../version";
import { registerMobileDevice } from "../mobileDevice";
import useIsMobile from "../hooks/useIsMobile";
import AnnouncementBar from "../components/AnnouncementBar";
import PortalEEditions from "./PortalEEditions";
import { proxyExternalImages, looksLikeHtml } from "../components/HtmlContent";
import { filesFromPaste, addWithinLimits, fmtFileSize, MAX_FILES, MAX_FILE_MB } from "../utils/pastedFiles";
import { splitBody, previewText } from "../components/conversation/Conversation";

const API_URL = process.env.REACT_APP_API_URL || "";

// Files attached to a ticket or reply: images show as pictures (a pasted screenshot
// should be SEEN, not hidden behind a link); everything else is a download chip.
function AttachmentList({ attachments }) {
  if (!attachments?.length) return null;
  const images = attachments.filter(a => a.mimetype?.startsWith("image/"));
  const others = attachments.filter(a => !a.mimetype?.startsWith("image/"));
  return (
    <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid rgba(255,255,255,0.08)" }}>
      {images.map((a, ai) => (
        <a key={`i${ai}`} href={`${API_URL}${a.url}`} target="_blank" rel="noreferrer" title={a.name} style={{ display:"block", marginBottom:8 }}>
          <img src={`${API_URL}${a.url}`} alt={a.name}
            style={{ maxWidth:"100%", maxHeight:360, borderRadius:8, border:"1px solid rgba(255,255,255,0.12)", display:"block" }}
            onError={e => { e.currentTarget.parentElement.style.display = "none"; }} />
        </a>
      ))}
      {others.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {others.map((a, ai) => (
            <a key={`f${ai}`} href={`${API_URL}${a.url}`} target="_blank" rel="noreferrer"
              style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 10px",
                background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.15)",
                borderRadius:6, fontSize:11, color:"rgba(255,255,255,0.7)", textDecoration:"none" }}>
              <Paperclip size={10}/>{a.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// Files waiting to be sent with a message: an "Attach files" button plus anything
// pasted into the message box (screenshots), shown as removable chips — images with
// a thumbnail so the person can see exactly what they pasted.
function PendingAttachments({ files, onAdd, onRemove, disabled }) {
  const inputRef = useRef(null);
  const [thumbs, setThumbs] = useState([]);
  useEffect(() => {
    const urls = files.map(f => (f.type || "").startsWith("image/") ? URL.createObjectURL(f) : null);
    setThumbs(urls);
    return () => urls.forEach(u => u && URL.revokeObjectURL(u));
  }, [files]);
  return (
    <div style={{ marginTop:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
        <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} style={{
          display:"inline-flex", alignItems:"center", gap:6, padding:"7px 12px",
          background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.14)", borderRadius:8,
          color:"rgba(255,255,255,0.75)", fontSize:12, fontWeight:600, cursor: disabled ? "default" : "pointer",
        }}>
          <Paperclip size={13}/> Attach files
        </button>
        <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>
          or paste a screenshot into the message box (Ctrl+V / ⌘V) · up to {MAX_FILES} files, {MAX_FILE_MB} MB each
        </span>
        <input ref={inputRef} type="file" multiple style={{ display:"none" }}
          onChange={e => { onAdd(Array.from(e.target.files || [])); e.target.value = ""; }} />
      </div>
      {files.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:10 }}>
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"5px 8px",
              background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.14)", borderRadius:8,
              fontSize:12, color:"rgba(255,255,255,0.8)" }}>
              {thumbs[i]
                ? <img src={thumbs[i]} alt="" style={{ width:36, height:36, objectFit:"cover", borderRadius:5 }} />
                : <Paperclip size={13}/>}
              <span style={{ maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
              <span style={{ color:"rgba(255,255,255,0.4)", fontSize:11 }}>{fmtFileSize(f.size)}</span>
              <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${f.name}`} disabled={disabled}
                style={{ background:"none", border:"none", color:"rgba(255,255,255,0.45)", cursor:"pointer", padding:0, display:"flex", alignItems:"center" }}>
                <X size={13}/>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Render a ticket message body. Inbound emails (Outlook, Gmail, …) arrive as full
// HTML carrying their own inline colors (usually black), so we render them on a white
// "email card" the way a dark-mode email client does — legible regardless of the dark
// portal theme. Plain-text / AI bodies render in the portal's normal dark style.
// Memoised for the same reason as HtmlContent: under React 19 a new {__html} object on every
// render rewrote the email (and re-fetched its broken images) on each keystroke of a reply.
// memo(): typing a reply never redraws the messages at all (Juwan 2026-10-27).
const MessageBody = memo(function MessageBody({ text }) {
  const normalized = text ? String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n") : "";
  const isHtml = looksLikeHtml(normalized);
  const html = useMemo(() => (isHtml ? { __html: proxyExternalImages(normalized) } : null), [isHtml, normalized]);
  if (!text) return null;
  if (html) {
    return (
      <div
        className="portal-email-html"
        style={{ background:"#fff", color:"#1a1a1a", borderRadius:8, padding:"12px 14px",
          lineHeight:1.6, overflowX:"auto", wordBreak:"break-word" }}
        dangerouslySetInnerHTML={html}
      />
    );
  }
  return <div style={{ whiteSpace:"pre-wrap" }}>{normalized}</div>;
});

// =============================================================
//  HELPERS
// =============================================================
const fmtDate   = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "—";
const fmtTime   = (d) => d ? new Date(d).toLocaleString("en-US",  { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" }) : "—";
const timeAgo   = (d) => {
  if (!d) return "—";
  const mins = Math.round((Date.now() - new Date(d)) / 60000);
  if (mins < 60)    return `${mins}m ago`;
  if (mins < 1440)  return `${Math.round(mins/60)}h ago`;
  return `${Math.round(mins/1440)}d ago`;
};

const PRIORITY_COLORS = {
  Critical: "#ef4444",
  High:     "#f97316",
  Medium:   "#eab308",
  Low:      "#22c55e",
};

// =============================================================
//  PORTAL LOGIN
// =============================================================
function PortalLogin({ branding, onLogin, expectedEmail = "" }) {
  const [email, setEmail]     = useState(expectedEmail || "");
  const [password, setPass]   = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const location = window.location;

  const primary  = branding.primary_color || "#4f6fff";
  const bg       = branding.accent_color  || "#0f172a";
  const logo     = branding.logo_url ? `${API_URL}${branding.logo_url}` : null;
  const name     = branding.company_name  || "Support Portal";
  const ssoEnabled = !!branding.sso_enabled;

  // Handle SSO redirect — backend sends ?token=... back to this page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("token");
    const ssoError = params.get("error");
    if (ssoError) {
      const msgs = { sso_failed:"Microsoft sign-in was cancelled.", domain_not_found:"Your email domain is not linked to this portal. Contact your admin.", sso_error:"Microsoft sign-in failed. Please try again." };
      setError(msgs[ssoError] || "Sign-in failed. Please try again.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (ssoToken) {
      try {
        // JWT uses base64url (- and _ instead of + and /). atob() only accepts standard
        // base64, so convert and pad before decoding.
        const b64 = ssoToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - b64.length % 4) % 4);
        const payload = JSON.parse(atob(padded));
        const contact = { id: payload.id, email: payload.email, clientId: payload.client_id, portalRole: payload.portal_role, firstName: payload.first_name || "", lastName: payload.last_name || "" };
        localStorage.setItem("midtc_portal_token",   ssoToken);
        localStorage.setItem("midtc_portal_contact", JSON.stringify(contact));
        window.history.replaceState({}, "", window.location.pathname);
        linkLocalDevice(ssoToken).catch(() => {}); // best-effort: link this machine to the user
        onLogin(contact);
      } catch { setError("Sign-in token was invalid. Please try again."); }
    }
  }, []);

  const submit = async () => {
    if (!email || !password) { setError("Email and password required"); return; }
    setLoading(true); setError("");
    try {
      const res = await portalLogin(email, password);
      if (res.data.contact.clientId !== branding.client_id) {
        setError("No access for this portal."); setLoading(false); return;
      }
      localStorage.setItem("midtc_portal_token",   res.data.token);
      localStorage.setItem("midtc_portal_contact", JSON.stringify(res.data.contact));
      linkLocalDevice(res.data.token).catch(() => {}); // best-effort: link this machine to the user
      onLogin(res.data.contact);
    } catch (err) {
      setError(err.response?.data?.error || "Login failed");
    }
    setLoading(false);
  };

  const signInWithMicrosoft = () => {
    const hint = expectedEmail ? `&login_hint=${encodeURIComponent(expectedEmail)}` : "";
    window.location.href = `${API_URL}/api/auth/m365/portal/login?return=${encodeURIComponent(window.location.pathname)}${hint}`;
  };

  return (
    <div style={{ minHeight:"100vh", background:bg, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:400 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          {logo
            ? <img src={logo} alt={name} style={{ maxHeight:56, maxWidth:200, objectFit:"contain", marginBottom:16 }} />
            : <div style={{ fontSize:24, fontWeight:800, color:"#fff", marginBottom:8 }}>{name}</div>
          }
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.5)" }}>Sign in to your support portal</div>
        </div>

        <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:16, padding:32, border:"1px solid rgba(255,255,255,0.08)" }}>
          {expectedEmail && (
            <div style={{ background:"rgba(29,78,216,0.12)", border:"1px solid rgba(59,130,246,0.35)", borderRadius:10, padding:"12px 14px", marginBottom:16, fontSize:12.5, color:"#bfdbfe", lineHeight:1.55, display:"flex", gap:9 }}>
              <Lock size={15} style={{ flexShrink:0, marginTop:1 }} />
              <span>This secure link is locked to <strong style={{ color:"#fff" }}>{expectedEmail}</strong>. Sign in with that account to view it.</span>
            </div>
          )}
          {error && (
            <div style={{ background:"#7f1d1d", color:"#fca5a5", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {/* Microsoft SSO button — shown when client has Azure tenant linked */}
          {ssoEnabled && (
            <>
              <button onClick={signInWithMicrosoft} style={{
                width:"100%", padding:"11px 16px", background:"#fff", border:"none",
                borderRadius:8, color:"#1a1a1a", fontSize:14, fontWeight:600,
                cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:16,
              }}>
                <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
                  <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </button>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
                <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.1)" }} />
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)" }}>or use password</span>
                <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.1)" }} />
              </div>
            </>
          )}

          <div style={{ marginBottom:16 }}>
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.5)", marginBottom:6 }}>Email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              placeholder="you@company.com"
              style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"10px 14px", color:"#fff", fontSize:14, outline:"none", boxSizing:"border-box" }}
            />
          </div>

          <div style={{ marginBottom:24 }}>
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.5)", marginBottom:6 }}>Password</label>
            <input
              type="password" value={password} onChange={e => setPass(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              placeholder="••••••••"
              style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"10px 14px", color:"#fff", fontSize:14, outline:"none", boxSizing:"border-box" }}
            />
          </div>

          <button onClick={submit} disabled={loading} style={{
            width:"100%", padding:"12px", background:primary, border:"none",
            borderRadius:8, color:"#fff", fontSize:14, fontWeight:700,
            cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1,
          }}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>

        <div style={{ textAlign:"center", marginTop:24, fontSize:12, color:"rgba(255,255,255,0.25)" }}>
          Powered by MIDTC Support Platform
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  SESSION EXPIRED — sign in again without leaving the page
// =============================================================
// Shown over whatever the person is doing when their session runs out, so a reply
// they were writing (and the files on it) survive: the page underneath stays mounted.
const SESSION_RESTORED_EVENT = "midtc:portal-session-restored";
function SessionExpiredModal({ branding, contact, onSignedIn, onSignOut }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const primary = branding.primary_color || "#4f6fff";
  const submit = async (e) => {
    e?.preventDefault();
    if (!password) { setError("Enter your password."); return; }
    setBusy(true); setError("");
    try {
      const res = await portalLogin(contact.email, password);
      if (res.data.contact.clientId !== branding.client_id) { setError("That account isn't part of this portal."); setBusy(false); return; }
      localStorage.setItem("midtc_portal_token", res.data.token);
      localStorage.setItem("midtc_portal_contact", JSON.stringify(res.data.contact));
      onSignedIn(res.data.contact);
    } catch (err) {
      setError(err.response?.data?.error || "That didn't work — check your password and try again.");
    }
    setBusy(false);
  };
  const microsoft = () => {
    const hint = contact.email ? `&login_hint=${encodeURIComponent(contact.email)}` : "";
    window.location.href = `${API_URL}/api/auth/m365/portal/login?return=${encodeURIComponent(window.location.pathname)}${hint}`;
  };
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="portal-session-title" style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(2,6,23,0.72)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <form onSubmit={submit} style={{ width:"100%", maxWidth:400, background:"#0f172a", border:"1px solid rgba(255,255,255,0.12)", borderRadius:16, padding:26, boxShadow:"0 24px 70px rgba(0,0,0,0.6)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
          <Lock size={18} color="#fde68a" />
          <h2 id="portal-session-title" style={{ margin:0, fontSize:17, fontWeight:700, color:"#fff" }}>Please sign in again</h2>
        </div>
        <p style={{ margin:"0 0 16px", fontSize:13, color:"rgba(255,255,255,0.65)", lineHeight:1.55 }}>
          For your security you were signed out after a long break. Sign in to carry on — <strong style={{ color:"#fff" }}>anything you were typing is still on the page.</strong>
        </p>
        {error && <div role="alert" style={{ background:"#7f1d1d", color:"#fca5a5", padding:"9px 12px", borderRadius:8, fontSize:12.5, marginBottom:12, display:"flex", gap:8, alignItems:"center" }}><AlertCircle size={14} /> {error}</div>}
        <label style={{ display:"block", fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.5)", marginBottom:6 }}>Email</label>
        <input value={contact.email || ""} readOnly style={{ width:"100%", boxSizing:"border-box", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"10px 12px", color:"rgba(255,255,255,0.7)", fontSize:14, marginBottom:12 }} />
        <label style={{ display:"block", fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.5)", marginBottom:6 }} htmlFor="portal-session-pw">Password</label>
        <input id="portal-session-pw" type="password" autoFocus value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
          style={{ width:"100%", boxSizing:"border-box", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.14)", borderRadius:8, padding:"10px 12px", color:"#fff", fontSize:14, outline:"none", marginBottom:16 }} />
        <button type="submit" disabled={busy} style={{ width:"100%", padding:"11px", background:primary, border:"none", borderRadius:8, color:"#fff", fontSize:14, fontWeight:700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1 }}>
          {busy ? "Signing in…" : "Sign in and continue"}
        </button>
        {branding.sso_enabled && (
          <>
            <button type="button" onClick={microsoft} style={{ width:"100%", marginTop:10, padding:"10px", background:"#fff", border:"none", borderRadius:8, color:"#1a1a1a", fontSize:13.5, fontWeight:600, cursor:"pointer" }}>
              Sign in with Microsoft instead
            </button>
            <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.45)", marginTop:8, lineHeight:1.5 }}>Microsoft sign-in reloads the page: your typed message is kept, but attached files will need adding again.</div>
          </>
        )}
        <div style={{ textAlign:"center", marginTop:14 }}>
          <button type="button" onClick={onSignOut} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.45)", fontSize:12, cursor:"pointer", textDecoration:"underline" }}>Sign out instead</button>
        </div>
      </form>
    </div>
  );
}

// =============================================================
//  PORTAL LAYOUT  (nav + outlet)
// =============================================================
function PortalLayout({ branding, contact, onLogout, children }) {
  const isMobile = useIsMobile();
  // Nav starts open on desktop, hidden on phones (where it overlays as a drawer)
  const [mobileOpen, setMobileOpen] = useState(() => !window.matchMedia("(max-width: 768px)").matches);
  const { identifier } = useParams();
  const location = useLocation();
  // E-Editions shows only for clients with papers set up for it (Champion Media, Times Leader, PAW).
  const [hasEEditions, setHasEEditions] = useState(false);
  useEffect(() => { PORTAL_API.get("/portal/eeditions").then((r) => setHasEEditions(!!r.data?.publications?.length)).catch(() => {}); }, []);
  const primary = branding.primary_color || "#4f6fff";
  const bg      = branding.accent_color  || "#0f172a";
  const logo    = branding.logo_url ? `${API_URL}${branding.logo_url}` : null;
  const name    = branding.company_name  || "Support Portal";
  const isAdmin = contact.portalRole === "admin";

  // Absolute paths — PortalLayout is rendered outside <Routes>, so relative
  // NavLinks would resolve against the current URL and double-append the
  // target segment on every click (e.g. .../dashboard/dashboard/dashboard).
  const base = `/clientportal/${identifier}`;
  const navItems = [
    { to: `${base}/dashboard`,        label:"Dashboard",  icon:LayoutDashboard },
    { to: `${base}/tickets`,          label:"My Tickets", icon:Ticket          },
    { to: `${base}/knowledge-base`,   label:"Knowledge",  icon:BookOpen        },
    ...(isAdmin ? [{ to:`${base}/devices`, label:"Devices", icon:Monitor }] : []),
    ...(hasEEditions ? [{ to:`${base}/eeditions`, label:"E-Editions", icon:Newspaper }] : []),
  ];

  const NavItems = () => navItems.map(({ to, label, icon:Icon }) => (
    <NavLink key={to} to={to}
      style={({ isActive }) => ({
        display:"flex", alignItems:"center", gap:10, padding:"10px 16px",
        color: isActive ? "#fff" : "rgba(255,255,255,0.55)",
        background: isActive ? "rgba(255,255,255,0.12)" : "none",
        borderRadius:8, fontSize:14, fontWeight: isActive ? 600 : 400,
        textDecoration:"none", transition:"all 0.15s",
      })}
      onClick={() => { if (isMobile) setMobileOpen(false); }}
    >
      <Icon size={17} /> {label}
    </NavLink>
  ));

  return (
    <div style={{ minHeight:"100vh", background:bg, display:"flex", flexDirection:"column" }}>
      {/* Top nav */}
      <header style={{ background:primary, padding:"0 24px", height:56, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, position:"sticky", top:0, zIndex:50 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={() => setMobileOpen(!mobileOpen)}
            style={{ background:"none", border:"none", color:"rgba(255,255,255,0.7)", cursor:"pointer", display:"flex", padding:4 }}>
            <Menu size={20} />
          </button>
          {logo
            ? <img src={logo} alt={name} style={{ height:32, maxWidth:140, objectFit:"contain" }} />
            : <span style={{ fontWeight:700, fontSize:16, color:"#fff" }}>{name}</span>
          }
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:13, color:"rgba(255,255,255,0.7)" }}>
            {contact.firstName} {contact.lastName}
          </span>
          <button onClick={onLogout} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:6, padding:"6px 10px", color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:12 }}>
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </header>

      <div style={{ display:"flex", flex:1 }}>
        {/* Backdrop — phone drawer only */}
        {isMobile && mobileOpen && (
          <div onClick={() => setMobileOpen(false)}
            style={{ position:"fixed", top:56, left:0, right:0, bottom:0, background:"rgba(2,6,23,0.6)", zIndex:55 }} />
        )}

        {/* Sidebar nav — inline on desktop, slide-over drawer on phones */}
        <aside style={{
          ...(isMobile ? {
            position:"fixed", top:56, bottom:0, left:0, zIndex:60,
            width:240,
            transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
            transition:"transform 0.22s ease",
            background: bg,
            boxShadow: mobileOpen ? "8px 0 40px rgba(0,0,0,0.5)" : "none",
            borderRight:"1px solid rgba(255,255,255,0.08)",
            padding:"12px 8px",
          } : {
            width: mobileOpen ? 220 : 0,
            background:"rgba(0,0,0,0.3)",
            overflow:"hidden",
            transition:"width 0.2s ease",
            flexShrink:0,
            padding: mobileOpen ? "12px 8px" : 0,
          }),
          display:"flex", flexDirection:"column", gap:2,
        }}>
          <NavItems />
          <div style={{ marginTop:"auto", padding:"10px 12px", fontSize:11, color:"rgba(255,255,255,0.4)" }}>Version {APP_VERSION}</div>
        </aside>

        {/* Page content */}
        <main style={{ flex:1, minWidth:0, padding: isMobile ? "16px 14px" : "24px 28px", maxWidth:1100, width:"100%" }}>
          {/* System announcements sent to the client portal (Admin Review → Announcements) — until acknowledged */}
          <AnnouncementBar kind="portal" refreshKey={location.pathname} style={{ marginBottom:16 }} />
          {children}
        </main>
      </div>
    </div>
  );
}

// =============================================================
//  END-USER TICKET STATE  (plain words, not Open / In Progress / SLA)
// =============================================================
// Every portal screen shows one of these. "Awaiting your reply" uses the same rule as
// the daily open-tickets digest email (still open + the latest public message came from
// support), so the email and the portal never disagree about what needs the user.
const STATE_STYLES = {
  waiting:   { label:"Awaiting your reply", color:"#fdba74", bg:"rgba(249,115,22,0.13)",  border:"rgba(249,115,22,0.38)" },
  support:   { label:"With support",        color:"#93c5fd", bg:"rgba(59,130,246,0.13)",  border:"rgba(59,130,246,0.35)" },
  hold:      { label:"On hold",             color:"#fde68a", bg:"rgba(234,179,8,0.11)",   border:"rgba(234,179,8,0.32)"  },
  scheduled: { label:"Scheduled",           color:"#c4b5fd", bg:"rgba(139,92,246,0.13)",  border:"rgba(139,92,246,0.35)" },
  resolved:  { label:"Resolved",            color:"#86efac", bg:"rgba(34,197,94,0.11)",   border:"rgba(34,197,94,0.32)"  },
  closed:    { label:"Closed",              color:"#94a3b8", bg:"rgba(148,163,184,0.09)", border:"rgba(148,163,184,0.25)" },
};
const STATE_HINTS = {
  waiting:   "Our team replied and is waiting on you.",
  support:   "Our team is working on it — no action needed from you.",
  hold:      "Paused for now — usually waiting on a vendor, a part or a scheduled visit.",
  scheduled: "Held until the time you picked, then sent to our team.",
  resolved:  "It closes automatically in 24 hours. Reply below if you still need help.",
  closed:    "Still having trouble? Reply below and the ticket reopens.",
};
const isClosedStatus = (s) => s === "Resolved" || s === "Closed";
function ticketStateKey(t) {
  if (t.status === "Closed")   return "closed";
  if (t.status === "Resolved") return "resolved";
  if (t.status === "On Hold" && t.scheduled_at && new Date(t.scheduled_at) > new Date()) return "scheduled";
  if (t.waiting_on_you)        return "waiting";
  if (t.status === "On Hold")  return "hold";
  return "support";
}

function StatePill({ stateKey, size = "md", cc = false }) {
  const base = STATE_STYLES[stateKey] || STATE_STYLES.support;
  // On a ticket someone else opened, "your reply" is the requester's, not the CC'd viewer's.
  const s = cc && stateKey === "waiting" ? { ...base, label: "Awaiting a reply" } : base;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding: size === "sm" ? "2px 9px" : "4px 11px",
      borderRadius:99, fontSize: size === "sm" ? 11 : 12, fontWeight:700, whiteSpace:"nowrap",
      color:s.color, background:s.bg, border:`1px solid ${s.border}` }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }} />
      {s.label}
    </span>
  );
}

// "CC'd" marker: the ticket was opened by someone else and this person is copied on it.
function CcPill({ size = "md", title }) {
  return (
    <span title={title || "You're copied (CC'd) on this ticket"} style={{ display:"inline-flex", alignItems:"center", gap:5, flexShrink:0,
      padding: size === "sm" ? "1px 8px" : "3px 10px", borderRadius:99, fontSize: size === "sm" ? 10.5 : 11.5, fontWeight:700, whiteSpace:"nowrap",
      color:"#bae6fd", background:"rgba(14,165,233,0.14)", border:"1px solid rgba(56,189,248,0.4)" }}>
      <Users size={size === "sm" ? 10 : 12} /> CC'd
    </span>
  );
}

// Branding colors are hex; anything else falls back to a neutral tint.
const withAlpha = (hex, a) => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return `rgba(255,255,255,${a})`;
  const h = m[1].length === 3 ? m[1].split("").map(c => c + c).join("") : m[1];
  const n = parseInt(h, 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
};
const initialsOf = (name) =>
  String(name || "").trim().split(/\s+/).slice(0, 2).map(w => (w[0] || "").toUpperCase()).join("") || "?";
const fullName = (c) => `${c?.firstName || ""} ${c?.lastName || ""}`.trim();

function Avatar({ name, color, size = 32 }) {
  return (
    <div aria-hidden="true" style={{ width:size, height:size, borderRadius:"50%", flexShrink:0,
      display:"flex", alignItems:"center", justifyContent:"center",
      background: color ? withAlpha(color, 0.2) : "rgba(255,255,255,0.08)",
      border:`1px solid ${color ? withAlpha(color, 0.45) : "rgba(255,255,255,0.14)"}`,
      color: color ? "#fff" : "rgba(255,255,255,0.7)", fontSize: Math.round(size * 0.37), fontWeight:700 }}>
      {initialsOf(name)}
    </div>
  );
}

const CARD = { background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12 };
const DIM  = "rgba(255,255,255,0.45)";

function SectionTitle({ icon:Icon, children, right }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, padding:"14px 18px",
      borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, fontWeight:700, color:"rgba(255,255,255,0.85)" }}>
        {Icon && <Icon size={15} color="rgba(255,255,255,0.5)" />} {children}
      </div>
      {right}
    </div>
  );
}

const LinkBtn = ({ onClick, children }) => (
  <button type="button" onClick={onClick} style={{ background:"none", border:"none", padding:0, cursor:"pointer",
    color:"rgba(255,255,255,0.55)", fontSize:12, fontWeight:600, display:"inline-flex", alignItems:"center", gap:4 }}>
    {children}
  </button>
);

// Long inbound emails (signatures, quoted history, big screenshots) used to push the
// whole conversation off-screen. Anything taller than `max` is clamped behind a
// "Show full message" toggle. The inner box is observed so late-loading images count.
const looksHtml = (text) => looksLikeHtml(text);
function Clamp({ children, max = 420, fade = "rgba(15,23,42,0.96)" }) {
  const innerRef = useRef(null);
  const [tall, setTall] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const check = () => setTall(el.offsetHeight > max + 80);
    check();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [max]);
  const clamped = tall && !open;
  return (
    <div>
      <div style={{ position:"relative", maxHeight: clamped ? max : "none", overflow:"hidden" }}>
        <div ref={innerRef}>{children}</div>
        {clamped && <div style={{ position:"absolute", left:0, right:0, bottom:0, height:90,
          background:`linear-gradient(rgba(0,0,0,0), ${fade})`, pointerEvents:"none" }} />}
      </div>
      {tall && (
        <button type="button" onClick={() => setOpen(o => !o)} style={{ marginTop:8, background:"rgba(255,255,255,0.06)",
          border:"1px solid rgba(255,255,255,0.12)", borderRadius:99, padding:"4px 12px", color:"rgba(255,255,255,0.7)",
          fontSize:11.5, fontWeight:600, cursor:"pointer" }}>
          {open ? "Show less" : "Show full message"}
        </button>
      )}
    </div>
  );
}

// =============================================================
//  PORTAL DASHBOARD
// =============================================================
// Personal first: what needs the user, what support said lately, their own computers,
// and where to find answers. Status notices come from the public /api/status feed.
function PortalDashboard({ branding, contact }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { identifier } = useParams();
  const base = `/clientportal/${identifier}`;
  const primary = branding.primary_color || "#4f6fff";
  const isAdmin = contact.portalRole === "admin";

  const [waiting,  setWaiting]  = useState(null);   // my tickets awaiting my reply
  const [counts,   setCounts]   = useState(null);   // my open / waiting / closed
  const [company,  setCompany]  = useState(null);   // admins: whole-company counts
  const [activity, setActivity] = useState(null);   // latest support replies to me
  const [devices,  setDevices]  = useState(null);   // my own computers
  const [articles, setArticles] = useState([]);
  const [notices,  setNotices]  = useState([]);     // services not operational
  const [kbQuery,  setKbQuery]  = useState("");

  useEffect(() => {
    getPortalTickets({ view:"waiting", scope:"mine", limit:8 }).then(r => setWaiting(r.data)).catch(() => setWaiting([]));
    getPortalTicketCounts({ scope:"mine" }).then(r => setCounts(r.data)).catch(() => {});
    if (isAdmin) getPortalTicketCounts({}).then(r => setCompany(r.data)).catch(() => {});
    getPortalActivity().then(r => setActivity(r.data)).catch(() => setActivity([]));
    getPortalDevices().then(r => setDevices((r.data || []).filter(d => d.contact_id === contact.id))).catch(() => setDevices([]));
    getPortalKBArticles().then(r => setArticles(r.data || [])).catch(() => {});
    fetch(`${API_URL}/api/status`).then(r => r.json())
      .then(s => setNotices((s.services || []).filter(x => x.status === "degraded" || x.status === "down")))
      .catch(() => {});
  }, [isAdmin, contact.id]);

  const popular = [...articles]
    .sort((a, b) => (b.view_count || 0) - (a.view_count || 0) || new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 4);
  const onlineCount = (devices || []).filter(d => d.status === "online").length;

  const StatCard = ({ label, value, color, onClick }) => (
    <button type="button" onClick={onClick} style={{ ...CARD, padding:"14px 18px", textAlign:"left", cursor: onClick ? "pointer" : "default" }}>
      <div style={{ fontSize:26, fontWeight:800, color: color || "#fff", lineHeight:1.1 }}>{value ?? "—"}</div>
      <div style={{ fontSize:12, color:DIM, marginTop:4 }}>{label}</div>
    </button>
  );

  return (
    <div>
      {/* Welcome */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, flexWrap:"wrap", marginBottom:20 }}>
        <div>
          <h2 style={{ margin:"0 0 4px", fontSize:22, fontWeight:700, color:"#fff" }}>Welcome back, {contact.firstName}</h2>
          <p style={{ margin:0, fontSize:14, color:DIM }}>{branding.welcome_msg || "Here's what's happening with your support."}</p>
        </div>
        <button onClick={() => navigate(`${base}/tickets/new`)} style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 18px",
          background:primary, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>
          <Plus size={15} /> New Ticket
        </button>
      </div>

      {/* Service notices */}
      {notices.map(n => (
        <div key={n.key} style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", padding:"11px 16px", marginBottom:12, borderRadius:10,
          background: n.status === "down" ? "rgba(239,68,68,0.12)" : "rgba(234,179,8,0.10)",
          border:`1px solid ${n.status === "down" ? "rgba(239,68,68,0.35)" : "rgba(234,179,8,0.32)"}`, fontSize:13, color:"rgba(255,255,255,0.85)" }}>
          <AlertTriangle size={15} color={n.status === "down" ? "#fca5a5" : "#fde68a"} />
          <span style={{ flex:1, minWidth:200 }}>
            <strong>{n.name}</strong> — {n.detail || (n.status === "down" ? "Service outage" : "Degraded performance")}
          </span>
          <a href="/status" target="_blank" rel="noreferrer" style={{ color:"rgba(255,255,255,0.7)", fontSize:12, fontWeight:600, display:"inline-flex", alignItems:"center", gap:4 }}>
            Service status <ExternalLink size={12} />
          </a>
        </div>
      ))}

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:12, marginBottom:20 }}>
        <StatCard label="My open tickets" value={counts?.open} color="#93c5fd" onClick={() => navigate(`${base}/tickets?view=open${isAdmin ? "&scope=mine" : ""}`)} />
        <StatCard label="Awaiting your reply" value={counts?.waiting} color={counts?.waiting ? "#fdba74" : "#86efac"}
          onClick={() => navigate(`${base}/tickets?view=waiting${isAdmin ? "&scope=mine" : ""}`)} />
        <StatCard label="My devices online" value={devices ? `${onlineCount}/${devices.length}` : null} color="#c4b5fd" />
        {isAdmin && <StatCard label={`Open at ${branding.company_name || "your company"}`} value={company?.open} onClick={() => navigate(`${base}/tickets?view=open`)} />}
      </div>

      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1.35fr) minmax(0,1fr)", gap:16, alignItems:"start" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:16, minWidth:0 }}>
          {/* Needs your attention */}
          <div style={CARD}>
            <SectionTitle icon={Inbox} right={<LinkBtn onClick={() => navigate(`${base}/tickets`)}>All tickets <ArrowRight size={12} /></LinkBtn>}>
              Needs your attention
            </SectionTitle>
            {waiting === null ? (
              <div style={{ padding:20, fontSize:13, color:DIM }}>Loading…</div>
            ) : waiting.length === 0 ? (
              <div style={{ padding:"18px 18px", display:"flex", alignItems:"center", gap:10, fontSize:13, color:"rgba(255,255,255,0.7)" }}>
                <CheckCircle2 size={18} color="#86efac" /> You're all caught up — nothing is waiting on your reply.
              </div>
            ) : waiting.map(t => (
              <div key={t.id} onClick={() => navigate(`${base}/tickets/${t.id}`)} className="portal-row"
                style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 18px", borderTop:"1px solid rgba(255,255,255,0.05)", cursor:"pointer" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
                    <span style={{ fontSize:13.5, fontWeight:600, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{t.subject}</span>
                    {t.is_cc && <CcPill size="sm" />}
                  </div>
                  <div style={{ fontSize:11.5, color:DIM, marginTop:2 }}>
                    {t.ticket_number} · {(t.last_reply_agent || "Support").trim()} replied {timeAgo(t.last_reply_at || t.last_activity_at)}
                  </div>
                </div>
                <span style={{ fontSize:12, fontWeight:700, color:"#fdba74", display:"inline-flex", alignItems:"center", gap:4, flexShrink:0 }}>
                  Reply <ChevronRight size={14} />
                </span>
              </div>
            ))}
          </div>

          {/* Latest from support */}
          <div style={CARD}>
            <SectionTitle icon={MessageSquare}>Latest from support</SectionTitle>
            {activity === null ? (
              <div style={{ padding:20, fontSize:13, color:DIM }}>Loading…</div>
            ) : activity.length === 0 ? (
              <div style={{ padding:"18px", fontSize:13, color:DIM }}>No replies from our team in the last 30 days.</div>
            ) : activity.map(a => (
              <div key={a.id} onClick={() => navigate(`${base}/tickets/${a.ticket_id}`)} className="portal-row"
                style={{ display:"flex", gap:12, padding:"12px 18px", borderTop:"1px solid rgba(255,255,255,0.05)", cursor:"pointer" }}>
                <Avatar name={a.agent_name} color={primary} size={30} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
                    <span style={{ fontSize:12.5, color:"rgba(255,255,255,0.85)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                      <strong>{(a.agent_name || "Support").trim()}</strong>
                      <span style={{ color:DIM }}> on {a.ticket_number} · {a.subject}</span>
                    </span>
                    <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", flexShrink:0 }}>{timeAgo(a.created_at)}</span>
                  </div>
                  <div style={{ fontSize:12.5, color:"rgba(255,255,255,0.6)", marginTop:3, lineHeight:1.5,
                    display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{a.snippet}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:16, minWidth:0 }}>
          {/* My devices */}
          <div style={CARD}>
            <SectionTitle icon={Laptop} right={isAdmin && <LinkBtn onClick={() => navigate(`${base}/devices`)}>All company devices <ArrowRight size={12} /></LinkBtn>}>
              My devices
            </SectionTitle>
            {devices === null ? (
              <div style={{ padding:20, fontSize:13, color:DIM }}>Loading…</div>
            ) : devices.length === 0 ? (
              <div style={{ padding:"16px 18px", fontSize:13, color:DIM, lineHeight:1.6 }}>
                No computers are linked to you yet. Install the desktop app on your computer and sign in with this account — it will show up here.
              </div>
            ) : devices.map(d => <DeviceHealthRow key={d.id} device={d} primary={primary}
                onHelp={() => navigate(`${base}/tickets/new?device=${encodeURIComponent(d.id)}`)} />)}
          </div>

          {/* Help & knowledge */}
          <div style={CARD}>
            <SectionTitle icon={LifeBuoy}>Help &amp; answers</SectionTitle>
            <form onSubmit={e => { e.preventDefault(); navigate(`${base}/knowledge-base${kbQuery.trim() ? `?q=${encodeURIComponent(kbQuery.trim())}` : ""}`); }}
              style={{ padding:"14px 18px 6px", display:"flex", gap:8 }}>
              <div style={{ position:"relative", flex:1 }}>
                <Search size={14} style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:DIM }} />
                <input value={kbQuery} onChange={e => setKbQuery(e.target.value)} placeholder="Search how-to articles"
                  style={{ width:"100%", boxSizing:"border-box", background:"rgba(0,0,0,0.25)", border:"1px solid rgba(255,255,255,0.12)",
                    borderRadius:8, padding:"9px 12px 9px 32px", color:"#fff", fontSize:13, outline:"none" }} />
              </div>
              <button type="submit" style={{ padding:"0 14px", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.14)",
                borderRadius:8, color:"rgba(255,255,255,0.8)", fontSize:12.5, fontWeight:600, cursor:"pointer" }}>Search</button>
            </form>
            {popular.length > 0 && (
              <div style={{ padding:"6px 8px 8px" }}>
                <div style={{ fontSize:10.5, fontWeight:700, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.07em", padding:"6px 10px" }}>Popular articles</div>
                {popular.map(a => (
                  <div key={a.id} onClick={() => navigate(`${base}/knowledge-base?article=${encodeURIComponent(a.id)}`)} className="portal-row"
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer" }}>
                    <BookOpen size={14} color="rgba(255,255,255,0.4)" style={{ flexShrink:0 }} />
                    <span style={{ flex:1, minWidth:0, fontSize:13, color:"rgba(255,255,255,0.8)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{a.title}</span>
                    <ChevronRight size={13} color="rgba(255,255,255,0.25)" />
                  </div>
                ))}
              </div>
            )}
            <div style={{ padding:"10px 18px 14px", borderTop:"1px solid rgba(255,255,255,0.05)", fontSize:12, color:DIM, display:"flex", alignItems:"center", gap:8 }}>
              {notices.length === 0
                ? <><CheckCircle2 size={14} color="#86efac" /> All services operational</>
                : <><AlertTriangle size={14} color="#fde68a" /> {notices.length} service notice{notices.length > 1 ? "s" : ""}</>}
              <a href="/status" target="_blank" rel="noreferrer" style={{ marginLeft:"auto", color:"rgba(255,255,255,0.6)", fontWeight:600, display:"inline-flex", alignItems:"center", gap:4 }}>
                Status page <ExternalLink size={11} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// One computer in "My devices": online state plus the few things a user can act on,
// in plain words — and a one-click ticket about that exact machine.
function DeviceHealthRow({ device: d, primary, onHelp }) {
  const online = d.status === "online";
  const issues = [];
  if (!online) issues.push(`Offline${d.last_seen ? ` · last seen ${timeAgo(d.last_seen)}` : ""}`);
  if (d.pending_updates > 0) issues.push(`${d.pending_updates} update${d.pending_updates > 1 ? "s" : ""} waiting`);
  if (d.av_status && !/^(enabled|on|active|running|up to date)$/i.test(d.av_status)) issues.push("Antivirus needs attention");
  if (d.disk_used_percent >= 90) issues.push(`Disk almost full (${Math.round(d.disk_used_percent)}%)`);
  const alerts = Array.isArray(d.alerts) ? d.alerts : (() => { try { return JSON.parse(d.alerts || "[]"); } catch { return []; } })();
  return (
    <div style={{ padding:"12px 18px", borderTop:"1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span title={online ? "Online" : "Offline"} style={{ width:8, height:8, borderRadius:"50%", flexShrink:0,
          background: online ? "#22c55e" : "#64748b", boxShadow: online ? "0 0 0 3px rgba(34,197,94,0.18)" : "none" }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13.5, fontWeight:600, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
            {d.device_name || d.hostname || d.computer_name || "Computer"}
          </div>
          <div style={{ fontSize:11.5, color:DIM, marginTop:1 }}>
            {[d.os, d.os_version].filter(Boolean).join(" ") || "Unknown OS"}
            {d.disk_free_gb != null && ` · ${Math.round(d.disk_free_gb)} GB free`}
          </div>
        </div>
        <button type="button" onClick={onHelp} style={{ flexShrink:0, padding:"6px 11px", background:withAlpha(primary, 0.16),
          border:`1px solid ${withAlpha(primary, 0.4)}`, borderRadius:8, color:"#fff", fontSize:11.5, fontWeight:600, cursor:"pointer",
          display:"inline-flex", alignItems:"center", gap:5 }}>
          <Wrench size={12} /> Get help
        </button>
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8, paddingLeft:18 }}>
        {issues.length === 0 && alerts.length === 0 ? (
          <span style={{ fontSize:11.5, color:"#86efac", display:"inline-flex", alignItems:"center", gap:5 }}>
            <ShieldCheck size={12} /> Looks healthy
          </span>
        ) : [...issues, ...alerts].slice(0, 4).map((x, i) => (
          <span key={i} style={{ fontSize:11, color:"#fde68a", background:"rgba(234,179,8,0.09)", border:"1px solid rgba(234,179,8,0.25)",
            borderRadius:99, padding:"2px 9px" }}>{x}</span>
        ))}
      </div>
    </div>
  );
}

// =============================================================
//  PORTAL TICKET LIST  (inbox: Open / Awaiting your reply / Closed)
// =============================================================
// Tab, search and (for portal admins) "mine vs everyone" live in the URL, so Back from
// a ticket returns to the same view. Filtering is server-side — a company can have
// thousands of closed tickets, far past what one page should load.
const LIST_PAGE = 50;
function PortalTicketList({ branding, contact }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { identifier } = useParams();
  const base = `/clientportal/${identifier}`;
  const primary = branding.primary_color || "#4f6fff";
  const isAdmin = contact.portalRole === "admin";
  const [params, setParams] = useSearchParams();
  const view  = ["open", "waiting", "closed"].includes(params.get("view")) ? params.get("view") : "open";
  const scope = isAdmin ? (params.get("scope") === "mine" ? "mine" : "company") : "mine";
  const query = params.get("q") || "";

  const [q, setQ]           = useState(query);
  const [rows, setRows]     = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const setParam = useCallback((key, value) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      if (value == null || value === "") p.delete(key); else p.set(key, value);
      return p;
    }, { replace: true });
  }, [setParams]);

  // Search as you type (debounced) — the URL is the source of truth for the fetch.
  useEffect(() => {
    const id = setTimeout(() => { if (q.trim() !== query) setParam("q", q.trim()); }, 300);
    return () => clearTimeout(id);
  }, [q, query, setParam]);

  const filters = useMemo(() => ({ q: query || undefined, scope: isAdmin ? scope : undefined }), [query, scope, isAdmin]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    Promise.all([getPortalTickets({ ...filters, view, limit: LIST_PAGE }), getPortalTicketCounts(filters)])
      .then(([r, c]) => { if (!live) return; setRows(r.data); setMore(r.data.length === LIST_PAGE); setCounts(c.data); })
      .catch(() => { if (live) setRows([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [filters, view]);

  const loadMore = () => {
    setLoadingMore(true);
    getPortalTickets({ ...filters, view, limit: LIST_PAGE, offset: rows.length })
      .then(r => { setRows(prev => [...prev, ...r.data]); setMore(r.data.length === LIST_PAGE); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const tabs = [
    { key:"open",    label:"Open",                count: counts?.open },
    { key:"waiting", label:"Awaiting your reply", count: counts?.waiting, hot: counts?.waiting > 0 },
    { key:"closed",  label:"Closed",              count: counts?.closed },
  ];
  const showSubmitter = isAdmin && scope === "company";
  const emptyText = query
    ? `No tickets match “${query}”.`
    : view === "waiting" ? "Nothing is waiting on your reply right now."
    : view === "closed"  ? "No closed tickets yet."
    : "No open tickets — everything is taken care of.";

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap", marginBottom:18 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"#fff" }}>{showSubmitter ? "Company tickets" : "My Tickets"}</h2>
          <div style={{ fontSize:13, color:DIM, marginTop:3 }}>
            {showSubmitter ? `Every ticket from ${branding.company_name || "your company"}.` : "Track your requests and reply to our team."}
          </div>
        </div>
        <button onClick={() => navigate(`${base}/tickets/new`)} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 18px",
          background:primary, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>
          <Plus size={14} /> New Ticket
        </button>
      </div>

      {/* Tabs + search (+ admin scope) */}
      <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:14 }}>
        <div role="tablist" style={{ display:"flex", gap:4, padding:4, background:"rgba(0,0,0,0.25)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:10, overflowX:"auto", maxWidth:"100%" }}>
          {tabs.map(t => {
            const active = view === t.key;
            return (
              <button key={t.key} role="tab" aria-selected={active} onClick={() => setParam("view", t.key === "open" ? null : t.key)} style={{
                display:"inline-flex", alignItems:"center", gap:7, padding:"7px 12px", borderRadius:7, border:"none", cursor:"pointer",
                whiteSpace:"nowrap", fontSize:12.5, fontWeight: active ? 700 : 500,
                background: active ? "rgba(255,255,255,0.12)" : "transparent", color: active ? "#fff" : "rgba(255,255,255,0.6)" }}>
                {t.label}
                {t.count != null && (
                  <span style={{ minWidth:18, padding:"1px 6px", borderRadius:99, fontSize:11, fontWeight:700, textAlign:"center",
                    background: t.hot ? "rgba(249,115,22,0.22)" : "rgba(255,255,255,0.08)", color: t.hot ? "#fdba74" : "rgba(255,255,255,0.6)" }}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ position:"relative", flex:"1 1 220px", minWidth:180 }}>
          <Search size={14} style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:DIM }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by subject or ticket number"
            style={{ width:"100%", boxSizing:"border-box", background:"rgba(0,0,0,0.25)", border:"1px solid rgba(255,255,255,0.1)",
              borderRadius:9, padding:"9px 32px 9px 32px", color:"#fff", fontSize:13, outline:"none" }} />
          {q && (
            <button type="button" aria-label="Clear search" onClick={() => setQ("")} style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)",
              background:"none", border:"none", color:DIM, cursor:"pointer", display:"flex", padding:2 }}><X size={14} /></button>
          )}
        </div>
        {isAdmin && (
          <div style={{ display:"flex", gap:4, padding:4, background:"rgba(0,0,0,0.25)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10 }}>
            {[["company", "Everyone", Users], ["mine", "Just mine", User]].map(([key, label, Icon]) => (
              <button key={key} onClick={() => setParam("scope", key === "company" ? null : key)} style={{ display:"inline-flex", alignItems:"center", gap:6,
                padding:"7px 11px", borderRadius:7, border:"none", cursor:"pointer", fontSize:12.5, whiteSpace:"nowrap",
                fontWeight: scope === key ? 700 : 500, background: scope === key ? "rgba(255,255,255,0.12)" : "transparent",
                color: scope === key ? "#fff" : "rgba(255,255,255,0.6)" }}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:48, color:"rgba(255,255,255,0.3)" }}>Loading tickets…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...CARD, textAlign:"center", padding:"44px 20px", color:DIM }}>
          {view === "waiting" && !query ? <CheckCircle2 size={34} color="#86efac" style={{ marginBottom:10 }} /> : <Ticket size={34} style={{ marginBottom:10, opacity:0.35 }} />}
          <div style={{ fontSize:14 }}>{emptyText}</div>
          {view === "open" && !query && (
            <button onClick={() => navigate(`${base}/tickets/new`)} style={{ marginTop:16, padding:"9px 20px", background:primary, border:"none",
              borderRadius:8, color:"#fff", fontSize:13, cursor:"pointer" }}>Submit a ticket</button>
          )}
        </div>
      ) : (
        <div style={{ ...CARD, overflow:"hidden" }}>
          {rows.map((t, i) => {
            const stateKey = ticketStateKey(t);
            const tech = (t.assigned_to_name || "").trim();
            const urgent = t.priority === "Critical" || t.priority === "High";
            const meta = [t.ticket_number, `Opened ${fmtDate(t.created_at)}`, (showSubmitter || t.is_cc) && !t.is_mine ? `by ${t.submitter_name || "a colleague"}` : null].filter(Boolean).join(" · ");
            return (
              <div key={t.id} onClick={() => navigate(`${base}/tickets/${t.id}`)} className="portal-row"
                style={{ display:"flex", alignItems: isMobile ? "flex-start" : "center", gap:14, padding: isMobile ? "13px 14px" : "13px 18px",
                  borderTop: i ? "1px solid rgba(255,255,255,0.05)" : "none", cursor:"pointer",
                  boxShadow: stateKey === "waiting" ? "inset 3px 0 0 #f97316" : "none" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
                    {urgent && <span title={`${t.priority} priority`} style={{ width:7, height:7, borderRadius:"50%", flexShrink:0, background:PRIORITY_COLORS[t.priority] }} />}
                    <span style={{ fontSize:14, fontWeight: stateKey === "waiting" ? 700 : 600, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{t.subject}</span>
                    {t.is_cc && <CcPill size="sm" title={`You're CC'd — ${t.submitter_name || "a colleague"} opened this ticket`} />}
                  </div>
                  <div style={{ fontSize:11.5, color:DIM, marginTop:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{meta}</div>
                  {isMobile && (
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:8 }}>
                      <StatePill stateKey={stateKey} size="sm" cc={t.is_cc} />
                      <span style={{ fontSize:11, color:DIM }}>{timeAgo(t.last_activity_at)}</span>
                    </div>
                  )}
                </div>
                {!isMobile && (
                  <>
                    <div style={{ width:148, flexShrink:0 }}><StatePill stateKey={stateKey} size="sm" cc={t.is_cc} /></div>
                    <div style={{ width:140, flexShrink:0, display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
                      {tech ? <><Avatar name={tech} color={primary} size={22} /><span style={{ fontSize:12, color:"rgba(255,255,255,0.65)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{tech}</span></>
                            : <span style={{ fontSize:12, color:"rgba(255,255,255,0.35)" }}>Being assigned</span>}
                    </div>
                    <div title={`${t.response_count} repl${Number(t.response_count) === 1 ? "y" : "ies"}`} style={{ width:44, flexShrink:0, display:"flex", alignItems:"center", gap:5, fontSize:12, color:"rgba(255,255,255,0.45)" }}>
                      <MessageSquare size={13} /> {t.response_count}
                    </div>
                    <div style={{ width:70, flexShrink:0, fontSize:12, color:"rgba(255,255,255,0.45)", textAlign:"right" }}>{timeAgo(t.last_activity_at)}</div>
                  </>
                )}
                <ChevronRight size={15} color="rgba(255,255,255,0.25)" style={{ flexShrink:0, marginTop: isMobile ? 2 : 0 }} />
              </div>
            );
          })}
          {more && (
            <div style={{ borderTop:"1px solid rgba(255,255,255,0.05)", padding:12, textAlign:"center" }}>
              <button onClick={loadMore} disabled={loadingMore} style={{ padding:"7px 18px", background:"rgba(255,255,255,0.06)",
                border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, color:"rgba(255,255,255,0.7)", fontSize:12.5, cursor:"pointer" }}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================
//  PORTAL TICKET DETAIL  (conversation + details panel)
// =============================================================
const RESOLVED_EVENT = /^✅ .+ marked this ticket as resolved from the support portal\.$/;

// Plain-language reason a send or upload failed. The page keeps the text and files.
function sendFailure(err, what = "Your reply") {
  const st = err?.response?.status;
  if (st === 401) return "You were signed out, so nothing was sent. Sign in again and press Send — your message and files are still here.";
  if (st === 413) return "Those files are too large to send together. Send fewer at a time (each file can be up to 20 MB).";
  if (!err?.response) return "The connection dropped while sending. Check your internet and press Send again — your message and files are still here.";
  return err.response?.data?.error || `${what} could not be sent — please try again.`;
}

function UploadProgress({ pct, label = "Uploading" }) {
  if (pct == null) return null;
  return (
    <div aria-live="polite" style={{ marginTop:10 }}>
      <div style={{ height:5, borderRadius:99, background:"rgba(255,255,255,0.1)", overflow:"hidden" }}>
        <div style={{ width:`${pct}%`, height:"100%", background:"#38bdf8", transition:"width .2s" }} />
      </div>
      <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.55)", marginTop:5 }}>{label}… {pct}%</div>
    </div>
  );
}

const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
const dayName = (d) => {
  const x = new Date(d), now = new Date(), y = new Date(); y.setDate(now.getDate() - 1);
  if (sameDay(x, now)) return "Today";
  if (sameDay(x, y)) return "Yesterday";
  return x.toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric", ...(x.getFullYear() !== now.getFullYear() ? { year:"numeric" } : {}) });
};

// One message body: the new words, with an email's quoted history and signature folded away.
// memo(): typing a reply never redraws the conversation (Juwan 2026-10-27).
const PortalMessageBody = memo(function PortalMessageBody({ text }) {
  const [quoted, setQuoted] = useState(false);
  const [sig, setSig] = useState(false);
  const parts = splitBody(text);
  const link = { background:"none", border:"none", padding:0, color:"#7dd3fc", fontSize:12.5, fontWeight:600, cursor:"pointer" };
  return (
    <>
      <Clamp fade={looksHtml(parts.main) ? "rgba(255,255,255,0.97)" : "rgba(15,23,42,0.96)"}>
        <div style={{ fontSize:14, color:"rgba(255,255,255,0.85)", lineHeight:1.65 }}><MessageBody text={parts.main} /></div>
      </Clamp>
      {(parts.quoted || parts.sig) && (
        <div style={{ display:"flex", gap:18, marginTop:10, flexWrap:"wrap" }}>
          {parts.quoted && <button type="button" style={link} aria-expanded={quoted} onClick={() => setQuoted(o => !o)}>{quoted ? "Hide earlier emails" : "Show earlier emails"}</button>}
          {parts.sig && <button type="button" style={link} aria-expanded={sig} onClick={() => setSig(o => !o)}>{sig ? "Hide signature" : "Show signature"}</button>}
        </div>
      )}
      {sig && parts.sig && <div style={{ marginTop:10, fontSize:13 }}><MessageBody text={parts.sig} /></div>}
      {quoted && parts.quoted && <div style={{ marginTop:10, paddingLeft:12, borderLeft:"3px solid rgba(255,255,255,0.18)", fontSize:13 }}><MessageBody text={parts.quoted} /></div>}
    </>
  );
});

// A fresh page per ticket, so a draft or open messages never carry over to another one.
function PortalTicketDetailKeyed(props) {
  const { ticketId } = useParams();
  return <PortalTicketDetail key={ticketId} {...props} />;
}

function PortalTicketDetail({ branding, contact }) {
  const { ticketId, identifier } = useParams();
  const base         = `/clientportal/${identifier}`;
  const navigate     = useNavigate();
  const isMobile     = useIsMobile();
  const draftKey     = `midtc_portal_draft_${ticketId}`;
  const [ticket,  setTicket]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [reply,   setReply]   = useState(() => { try { return sessionStorage.getItem(draftKey) || ""; } catch { return ""; } });
  const [sending, setSending] = useState(false);
  const [files,   setFiles]   = useState([]);   // attachments waiting to go with the reply
  const [notice,  setNotice]  = useState("");   // why a file was left out / why sending failed
  const [sentOk,  setSentOk]  = useState("");
  const [pct,     setPct]     = useState(null); // upload progress
  const [confirmResolve, setConfirmResolve] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [open, setOpen] = useState(null);       // Set of open message ids (null until first load)
  const composerRef = useRef(null);
  const primary = branding.primary_color || "#4f6fff";

  // Keep what is typed if the page is left, reloaded or the session has to be renewed.
  useEffect(() => { try { if (reply) sessionStorage.setItem(draftKey, reply); else sessionStorage.removeItem(draftKey); } catch { /* ignore */ } }, [reply, draftKey]);

  const addFiles = (incoming) => {
    const r = addWithinLimits(files, incoming);
    setFiles(r.files);
    setNotice(r.notes.join(" "));
  };
  const removeFile = (i) => setFiles(prev => prev.filter((_, j) => j !== i));
  // A screenshot pasted into the box becomes an attachment; a text paste is left alone.
  const onPaste = (e) => {
    const pasted = filesFromPaste(e);
    if (!pasted.length) return;
    e.preventDefault();
    addFiles(pasted);
  };

  // `silent` refreshes the thread in the background (polling) without flashing the
  // loading spinner — so new replies appear on the customer's screen automatically.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return getPortalTicket(ticketId)
      .then(r => { setTicket(r.data); setLoadErr(""); setLoading(false); })
      .catch(err => {
        if (!silent) setLoadErr(err.response?.status === 403 ? "You don't have access to this ticket." : err.response?.status === 404 ? "This ticket wasn't found." : err.response?.status === 401 ? "" : "The ticket couldn't be loaded — please refresh.");
        setLoading(false);
      });
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  // Back from signing in again: fetch what was missed.
  useEffect(() => {
    const again = () => load(true);
    window.addEventListener(SESSION_RESTORED_EVENT, again);
    return () => window.removeEventListener(SESSION_RESTORED_EVENT, again);
  }, [load]);
  // Auto-update: poll every 15s while the tab is visible, and refresh on return to the tab.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") load(true); };
    const id = setInterval(tick, 15000);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", tick); };
  }, [load]);

  const sendReply = async () => {
    if (sending || (!reply.trim() && !files.length)) return;
    setSending(true); setNotice(""); setSentOk("");
    try {
      let res;
      if (files.length) {
        const fd = new FormData();
        fd.append("body", reply);
        files.forEach(f => fd.append("attachments", f));
        setPct(0);
        res = await addPortalResponse(ticketId, fd, { onUploadProgress: (p) => { if (p.total) setPct(Math.round((p.loaded / p.total) * 100)); } });
      } else {
        res = await addPortalResponse(ticketId, { body: reply });
      }
      const blocked = res?.data?.quarantined?.length || 0;
      const sentFiles = files.length - blocked;
      setReply("");
      setFiles([]);
      setSentOk(`Sent${sentFiles > 0 ? ` with ${sentFiles} file${sentFiles > 1 ? "s" : ""}` : ""}. Our team has been notified.`);
      if (blocked) setNotice(`${blocked} file${blocked > 1 ? "s were" : " was"} blocked by the malware scanner and not attached.`);
      await load(true);
    } catch (err) {
      setNotice(sendFailure(err));
    }
    setPct(null);
    setSending(false);
  };
  const canSend = !sending && (reply.trim() || files.length > 0);

  const resolve = async () => {
    setResolving(true);
    setNotice("");
    try {
      await resolvePortalTicket(ticketId);
      setConfirmResolve(false);
      await load(true);
    } catch (err) {
      setNotice(err.response?.status === 401 ? "You were signed out — sign in again and try once more." : err.response?.data?.error || "The ticket could not be marked resolved — please try again.");
    }
    setResolving(false);
  };

  // Back keeps the list's tab/search when we came from it; a link from an email has
  // no portal history, so go to the list instead of leaving the portal.
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(`${base}/tickets`));
  const toComposer = () => {
    const el = composerRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior:"smooth", block:"center" });
    el.querySelector("textarea")?.focus({ preventScroll:true });
  };

  const messages = useMemo(() => {
    if (!ticket) return [];
    const mine = ticket.contact_id === contact.id;
    const myName = fullName(contact) || "You";
    return [
      { id:"original", kind:"customer", name: mine ? "You" : (ticket.submitter_name || "Requester"), avatarName: mine ? myName : ticket.submitter_name,
        time: ticket.created_at, body: ticket.description, attachments: ticket.attachments, original: true,
        // Who was on the ORIGINAL email (kept since 2026-10-23) — not today's CC list.
        cc: Array.isArray(ticket.original_cc_emails) && ticket.original_cc_emails.length ? ticket.original_cc_emails : null },
      ...(ticket.responses || []).map(r => r.agent_name
        ? { id:r.id, kind:"support", name:r.agent_name.trim(), avatarName:r.agent_name, time:r.created_at, body:r.body, attachments:r.attachments }
        : { id:r.id, kind:"customer",
            name: r.contact_id === contact.id ? "You" : (r.contact_name || r.sender_name || "Customer"),
            avatarName: r.contact_id === contact.id ? myName : (r.contact_name || r.sender_name),
            copied: r.contact_id ? r.contact_id !== ticket.contact_id && r.contact_id !== contact.id : !!r.sender_name,
            time:r.created_at, body:r.body, attachments:r.attachments,
            event: RESOLVED_EVENT.test(String(r.body || "").trim()) }),
    ];
  }, [ticket, contact]);

  // First load: the original request and the two newest messages are open, the rest
  // fold to one line each. Anything that arrives later opens itself.
  const seen = useRef(null);
  useEffect(() => {
    if (!messages.length) return;
    if (!seen.current) {
      const o = new Set(["original", ...messages.slice(-2).map(m => m.id)]);
      setOpen(o);
      seen.current = new Set(messages.map(m => m.id));
      return;
    }
    const fresh = messages.filter(m => !seen.current.has(m.id));
    messages.forEach(m => seen.current.add(m.id));
    if (fresh.length) setOpen(prev => { const n = new Set(prev || []); fresh.forEach(m => n.add(m.id)); return n; });
  }, [messages]);
  const toggle = (mid) => setOpen(prev => { const n = new Set(prev || []); n.has(mid) ? n.delete(mid) : n.add(mid); return n; });

  if (loading) return <div style={{ padding:48, textAlign:"center", color:"rgba(255,255,255,0.3)" }}>Loading ticket...</div>;
  if (!ticket) return (
    <div style={{ padding:48, textAlign:"center", color:"#fca5a5" }}>
      {loadErr || "This ticket couldn't be loaded."}
      <div style={{ marginTop:14 }}><button type="button" onClick={() => navigate(`${base}/tickets`)} style={{ padding:"8px 16px", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.14)", borderRadius:8, color:"#fff", fontSize:13, cursor:"pointer" }}>Back to my tickets</button></div>
    </div>
  );

  const closed   = isClosedStatus(ticket.status);
  const lastMsg  = ticket.responses?.[ticket.responses.length - 1];
  const stateKey = ticketStateKey({ ...ticket, waiting_on_you: !closed && !!lastMsg?.agent_name });
  const state    = STATE_STYLES[stateKey];
  const mine     = ticket.contact_id === contact.id;
  const tech     = (ticket.assigned_to_name || "").trim();
  const openSet  = open || new Set(["original"]);
  const openAll  = () => setOpen(new Set(messages.map(m => m.id)));
  const allOpen  = messages.every(m => m.event || openSet.has(m.id));

  const sla = !closed && ticket.sla_due_at
    ? ticket.sla_status === "hold"   ? { text:"Paused", color:"#fde68a" }
    : ticket.sla_status === "breach" ? { text:"Past target — we're on it", color:"#fdba74" }
    : { text:`On track · ${fmtTime(ticket.sla_due_at)}`, color:"#86efac" }
    : null;

  const DetailRow = ({ label, children }) => (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, padding:"8px 0", borderTop:"1px solid rgba(255,255,255,0.05)" }}>
      <span style={{ fontSize:11.5, color:DIM, flexShrink:0 }}>{label}</span>
      <span style={{ fontSize:12.5, color:"rgba(255,255,255,0.85)", textAlign:"right", minWidth:0, wordBreak:"break-word" }}>{children}</span>
    </div>
  );

  const details = (
    <div style={{ ...CARD, padding:"14px 16px" }}>
      <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Ticket details</div>
      <DetailRow label="Ticket">{ticket.ticket_number}</DetailRow>
      <DetailRow label="Status"><StatePill stateKey={stateKey} size="sm" cc={ticket.is_cc} /></DetailRow>
      <DetailRow label="Technician">
        {tech ? <span style={{ display:"inline-flex", alignItems:"center", gap:7 }}><Avatar name={tech} color={primary} size={20} />{tech}</span>
              : <span style={{ color:DIM }}>Being assigned</span>}
      </DetailRow>
      {!mine && <DetailRow label="Opened by">{ticket.submitter_name || "—"}</DetailRow>}
      <DetailRow label="Opened">{fmtTime(ticket.created_at)}</DetailRow>
      <DetailRow label="Last update">{timeAgo(ticket.last_activity_at)}</DetailRow>
      <DetailRow label="Priority">
        <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:PRIORITY_COLORS[ticket.priority] || "#94a3b8" }} />{ticket.priority}
        </span>
      </DetailRow>
      {ticket.category && <DetailRow label="Category">{ticket.category}</DetailRow>}
      {ticket.device_name && <DetailRow label="Device">{ticket.device_name}</DetailRow>}
      {sla && <DetailRow label="Service target"><span style={{ color:sla.color }}>{sla.text}</span></DetailRow>}
      <div style={{ paddingTop:8, borderTop:"1px solid rgba(255,255,255,0.05)" }}>
        <div style={{ fontSize:11.5, color:DIM, marginBottom:6 }}>Copied on replies (CC)</div>
        {Array.isArray(ticket.cc_emails) && ticket.cc_emails.length > 0 ? (
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {ticket.cc_emails.map(e => {
              const me = String(e).toLowerCase() === String(contact.email || "").toLowerCase();
              return (
                <span key={e} style={{ fontSize:11, color: me ? "#bae6fd" : "rgba(255,255,255,0.65)", background: me ? "rgba(14,165,233,0.14)" : "rgba(255,255,255,0.06)",
                  border:`1px solid ${me ? "rgba(56,189,248,0.4)" : "rgba(255,255,255,0.1)"}`, borderRadius:99, padding:"2px 9px", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis" }}>
                  {me ? `${e} (you)` : e}
                </span>
              );
            })}
          </div>
        ) : <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>Nobody else is copied.</div>}
      </div>
    </div>
  );

  let lastDay = null;
  const thread = [];
  for (const m of messages) {
    if (m.time && !sameDay(m.time, lastDay || 0)) {
      lastDay = m.time;
      thread.push(
        <div key={`d-${m.id}`} style={{ display:"flex", alignItems:"center", gap:12, fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"rgba(255,255,255,0.4)", margin:"10px 0 0" }}>
          {dayName(m.time)}<span style={{ flex:1, height:1, background:"rgba(255,255,255,0.08)" }} />
        </div>
      );
    }
    if (m.event) {
      thread.push(
        <div key={m.id} style={{ display:"flex", alignItems:"center", gap:10, fontSize:12, color:"#86efac", padding:"2px 4px" }}>
          <div style={{ flex:1, height:1, background:"rgba(34,197,94,0.25)" }} />
          <span style={{ display:"inline-flex", alignItems:"center", gap:6, textAlign:"center" }}>
            <CheckCircle size={13} /> {m.name} marked this resolved · {fmtTime(m.time)}
          </span>
          <div style={{ flex:1, height:1, background:"rgba(34,197,94,0.25)" }} />
        </div>
      );
      continue;
    }
    const isOpen = openSet.has(m.id);
    const support = m.kind === "support";
    const cardStyle = { flex:1, minWidth:0, borderRadius:12,
      background: support ? withAlpha(primary, 0.09) : "rgba(255,255,255,0.035)",
      border:`1px solid ${support ? withAlpha(primary, 0.3) : "rgba(255,255,255,0.08)"}` };
    const tags = (
      <>
        {support && <span style={{ fontSize:10.5, fontWeight:700, color:"#fff", background:withAlpha(primary, 0.35), borderRadius:4, padding:"1px 6px" }}>Support</span>}
        {m.original && <span style={{ fontSize:10.5, fontWeight:600, color:DIM, border:"1px solid rgba(255,255,255,0.14)", borderRadius:4, padding:"0 6px" }}>Original request</span>}
        {m.copied && <CcPill size="sm" title="Copied on this ticket" />}
      </>
    );
    thread.push(
      <div key={m.id} style={{ display:"flex", gap: isMobile ? 9 : 12 }}>
        <Avatar name={m.avatarName || m.name} color={support ? primary : null} size={isMobile ? 28 : 34} />
        {isOpen ? (
          <div style={{ ...cardStyle, padding: isMobile ? "10px 12px" : "12px 16px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:8, flexWrap:"wrap" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:8, minWidth:0, flexWrap:"wrap" }}>
                <span style={{ fontSize:13.5, fontWeight:700, color:"#fff" }}>{m.name}</span>{tags}
              </span>
              <span style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:11.5, color:"rgba(255,255,255,0.4)" }}>{fmtTime(m.time)}</span>
                {!m.original || messages.length > 1 ? (
                  <button type="button" onClick={() => toggle(m.id)} aria-expanded="true" aria-label="Fold this message" title="Fold"
                    style={{ background:"none", border:"none", color:"rgba(255,255,255,0.45)", cursor:"pointer", padding:2, display:"flex" }}><X size={14} /></button>
                ) : null}
              </span>
            </div>
            {m.original && m.cc && <div style={{ fontSize:12, color:DIM, marginBottom:8 }}>Cc {m.cc.join(", ")}</div>}
            <PortalMessageBody text={m.body} />
            <AttachmentList attachments={m.attachments} />
          </div>
        ) : (
          <button type="button" onClick={() => toggle(m.id)} aria-expanded="false" aria-label={`Open the message from ${m.name}, ${fmtTime(m.time)}`}
            className="portal-row" style={{ ...cardStyle, display:"flex", alignItems:"center", gap:10, padding:"10px 14px", textAlign:"left", cursor:"pointer", color:"inherit", fontFamily:"inherit" }}>
            <span style={{ fontSize:13, fontWeight:700, color:"#fff", whiteSpace:"nowrap" }}>{m.name}</span>{tags}
            <span style={{ flex:1, minWidth:0, fontSize:13, color:"rgba(255,255,255,0.55)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{previewText(m.body, 140)}</span>
            {m.attachments?.length > 0 && <Paperclip size={13} color="rgba(255,255,255,0.4)" style={{ flexShrink:0 }} />}
            {!isMobile && <span style={{ fontSize:11.5, color:"rgba(255,255,255,0.4)", whiteSpace:"nowrap" }}>{fmtTime(m.time)}</span>}
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <button onClick={goBack} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.5)", cursor:"pointer", fontSize:13,
        marginBottom:14, padding:0, display:"inline-flex", alignItems:"center", gap:6 }}>
        <ArrowLeft size={14} /> All tickets
      </button>

      {/* Title */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:12, color:DIM, marginBottom:4 }}>{ticket.ticket_number} · Opened {fmtDate(ticket.created_at)}{!mine ? ` by ${ticket.submitter_name || "a colleague"}` : ""}</div>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <h2 style={{ margin:0, fontSize: isMobile ? 18 : 21, fontWeight:700, color:"#fff", lineHeight:1.3, wordBreak:"break-word" }}>{ticket.subject}</h2>
          {ticket.is_cc && <CcPill />}
        </div>
      </div>

      {ticket.is_cc && (
        <div role="note" style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"12px 16px", borderRadius:10, marginBottom:12,
          background:"rgba(14,165,233,0.08)", border:"1px solid rgba(56,189,248,0.3)", fontSize:13, color:"rgba(255,255,255,0.85)", lineHeight:1.55 }}>
          <Users size={16} color="#7dd3fc" style={{ flexShrink:0, marginTop:2 }} />
          <span><strong style={{ color:"#bae6fd" }}>You're CC'd on this ticket.</strong> {ticket.submitter_name || "A colleague"} opened it. You get the email updates and can reply here; only {ticket.submitter_name || "they"} can mark it resolved.</span>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) 290px", gap:20, alignItems:"start" }}>
        <div style={{ minWidth:0 }}>
          {/* What's happening + the actions that go with it */}
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", padding:"12px 16px", borderRadius:10,
            background:state.bg, border:`1px solid ${state.border}`, marginBottom: confirmResolve ? 8 : 16 }}>
            <div style={{ flex:"1 1 220px", fontSize:13, color:"rgba(255,255,255,0.85)", lineHeight:1.5 }}>
              {ticket.is_cc && stateKey === "waiting"
                ? <><strong style={{ color:state.color }}>Awaiting a reply.</strong> Our team replied and is waiting on {ticket.submitter_name || "the person who opened it"} — you can reply too.</>
                : <><strong style={{ color:state.color }}>{state.label}.</strong> {STATE_HINTS[stateKey]}</>}
            </div>
            {ticket.can_reply && stateKey === "waiting" && (
              <button onClick={toComposer} style={{ padding:"7px 14px", background:"#f97316", border:"none", borderRadius:8, color:"#fff",
                fontSize:12.5, fontWeight:700, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6 }}>
                <Send size={12} /> Reply
              </button>
            )}
            {ticket.can_resolve && !closed && !confirmResolve && (
              <button onClick={() => setConfirmResolve(true)} style={{ padding:"7px 14px", background:"rgba(34,197,94,0.12)",
                border:"1px solid rgba(34,197,94,0.4)", borderRadius:8, color:"#86efac", fontSize:12.5, fontWeight:700, cursor:"pointer",
                display:"inline-flex", alignItems:"center", gap:6 }}>
                <CheckCircle size={13} /> Mark as resolved
              </button>
            )}
          </div>
          {confirmResolve && (
            <div style={{ padding:"14px 16px", borderRadius:10, background:"rgba(34,197,94,0.07)", border:"1px solid rgba(34,197,94,0.3)", marginBottom:16 }}>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.85)", lineHeight:1.55, marginBottom:12 }}>
                <strong>Is your issue fixed?</strong> Your technician will see that you marked it resolved, and the ticket closes after 24 hours.
                You can still reply to reopen it.
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <button onClick={resolve} disabled={resolving} style={{ padding:"8px 16px", background:"#16a34a", border:"none", borderRadius:8,
                  color:"#fff", fontSize:12.5, fontWeight:700, cursor: resolving ? "default" : "pointer", opacity: resolving ? 0.7 : 1 }}>
                  {resolving ? "Saving…" : "Yes, it's fixed"}
                </button>
                <button onClick={() => setConfirmResolve(false)} disabled={resolving} style={{ padding:"8px 16px", background:"rgba(255,255,255,0.06)",
                  border:"1px solid rgba(255,255,255,0.14)", borderRadius:8, color:"rgba(255,255,255,0.7)", fontSize:12.5, cursor:"pointer" }}>
                  Not yet
                </button>
              </div>
            </div>
          )}

          {messages.length > 3 && (
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
              <button type="button" onClick={() => allOpen ? setOpen(new Set(["original"])) : openAll()} style={{ background:"none", border:"none", color:"#7dd3fc", fontSize:12.5, fontWeight:600, cursor:"pointer" }}>
                {allOpen ? "Fold older messages" : `Open all ${messages.length} messages`}
              </button>
            </div>
          )}

          {/* Conversation — oldest first; the original request is the first message */}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {thread}
            {(ticket.responses || []).length === 0 && (
              <div style={{ padding:"6px 4px", fontSize:12.5, color:DIM }}>No replies yet — our team usually responds within a few business hours.</div>
            )}
          </div>

          {/* Reply — the submitter and anyone CC'd; replying to a closed ticket reopens it */}
          {ticket.can_reply ? (
            <div ref={composerRef} style={{ ...CARD, padding:16, marginTop:18 }}>
              <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:10, flexWrap:"wrap", marginBottom:8 }}>
                <span style={{ fontSize:12.5, fontWeight:700, color:"rgba(255,255,255,0.75)" }}>{closed ? "Reply to reopen this ticket" : "Reply to our team"}</span>
                {Array.isArray(ticket.cc_emails) && ticket.cc_emails.length > 0 && (
                  <span style={{ fontSize:11.5, color:DIM }}>Everyone copied on the ticket sees replies by email</span>
                )}
              </div>
              <textarea
                value={reply}
                onChange={e => setReply(e.target.value)}
                onPaste={onPaste}
                onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); if (canSend) sendReply(); } }}
                aria-label={closed ? "Reply to reopen this ticket" : "Reply to our team"}
                placeholder={closed ? "Tell us what's still wrong — sending this reopens the ticket." : "Type your reply… (you can paste screenshots straight in)"}
                rows={4}
                style={{ width:"100%", background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"12px 14px",
                  color:"#fff", fontSize:14, lineHeight:1.55, resize:"vertical", outline:"none", boxSizing:"border-box" }}
              />
              <PendingAttachments files={files} onAdd={addFiles} onRemove={removeFile} disabled={sending} />
              <UploadProgress pct={pct} label="Uploading your files" />
              {notice && (
                <div role="alert" style={{ display:"flex", alignItems:"flex-start", gap:6, marginTop:10, fontSize:12.5, color:"#fcd34d", lineHeight:1.5 }}>
                  <AlertCircle size={14} style={{ flexShrink:0, marginTop:2 }} /> {notice}
                </div>
              )}
              {sentOk && !notice && (
                <div role="status" style={{ display:"flex", alignItems:"center", gap:6, marginTop:10, fontSize:12.5, color:"#86efac" }}>
                  <CheckCircle2 size={14} /> {sentOk}
                </div>
              )}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:12, marginTop:12 }}>
                {!isMobile && <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)" }}>Ctrl + Enter to send</span>}
                <button onClick={sendReply} disabled={!canSend} style={{
                  display:"flex", alignItems:"center", gap:8, padding:"9px 20px",
                  background: canSend ? primary : "rgba(255,255,255,0.08)",
                  border:"none", borderRadius:8, color: canSend ? "#fff" : "rgba(255,255,255,0.3)",
                  fontSize:13, fontWeight:600, cursor: canSend ? "pointer" : "default",
                }}>
                  <Send size={13} /> {sending ? (files.length ? "Uploading…" : "Sending…") : closed ? "Send & reopen" : "Send reply"}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginTop:18, padding:"12px 16px", borderRadius:10,
              background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", fontSize:12.5, color:DIM, lineHeight:1.55 }}>
              <Lock size={14} style={{ flexShrink:0, marginTop:2 }} />
              <span>Only {ticket.submitter_name || "the person who opened it"} and people copied on it can reply to this ticket. You can see it because you manage {branding.company_name || "your company"}'s support portal.</span>
            </div>
          )}
        </div>

        <aside style={{ position: isMobile ? "static" : "sticky", top:76, minWidth:0 }}>{details}</aside>
      </div>
    </div>
  );
}

// =============================================================
//  PORTAL NEW TICKET FORM
// =============================================================
function PortalNewTicket({ branding }) {
  const navigate  = useNavigate();
  const { identifier } = useParams();
  const base = `/clientportal/${identifier}`;
  const primary   = branding.primary_color || "#4f6fff";
  const [form, setForm] = useState({ subject:"", description:"", category:"General", priority:"Medium", cc_emails:[], scheduled_at:"" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [created, setCreated] = useState(null); // submitted-ticket confirmation
  const [files, setFiles] = useState([]);       // attachments to upload with the new ticket
  const [fileNotice, setFileNotice] = useState("");
  const [ccError, setCcError] = useState("");
  const [pct, setPct] = useState(null);         // upload progress for the files
  // The typed subject and description survive a reload or a sign-in prompt.
  useEffect(() => {
    try { const d = JSON.parse(sessionStorage.getItem("midtc_portal_newticket") || "null"); if (d) setForm(f => ({ ...f, subject: d.subject || f.subject, description: d.description || "" })); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try {
      if (form.subject || form.description) sessionStorage.setItem("midtc_portal_newticket", JSON.stringify({ subject: form.subject, description: form.description }));
      else sessionStorage.removeItem("midtc_portal_newticket");
    } catch { /* ignore */ }
  }, [form.subject, form.description]);
  // Dashboard "Get help" on a device opens this form with ?device=<id>: link the ticket
  // to that machine (the server only accepts a device of this company) and prefill.
  const [searchParams] = useSearchParams();
  const deviceParam = searchParams.get("device");
  const [device, setDevice] = useState(null);
  useEffect(() => {
    if (!deviceParam) return;
    getPortalDevices().then(r => {
      const d = (r.data || []).find(x => String(x.id) === deviceParam);
      if (!d) return;
      const name = d.device_name || d.hostname || d.computer_name || "my computer";
      setDevice({ id: d.id, name });
      setForm(f => ({ ...f, subject: f.subject || `Help with ${name}`, category: f.category === "General" ? "Hardware" : f.category }));
    }).catch(() => {});
  }, [deviceParam]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addFiles = (incoming) => {
    const r = addWithinLimits(files, incoming);
    setFiles(r.files);
    setFileNotice(r.notes.join(" "));
  };
  const removeFile = (i) => setFiles(prev => prev.filter((_, j) => j !== i));
  const onPaste = (e) => {
    const pasted = filesFromPaste(e);
    if (!pasted.length) return;
    e.preventDefault();
    addFiles(pasted);
  };

  const isEmailAddr = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const addCc = () => {
    const email = ccInput.trim().toLowerCase().replace(/[,;]+$/, "");
    if (!email) return;
    if (!isEmailAddr(email)) { setCcError("That doesn't look like an email address."); return; }
    setCcError("");
    if (!form.cc_emails.includes(email)) set("cc_emails", [...form.cc_emails, email]);
    setCcInput("");
  };

  const removeCc = (email) => set("cc_emails", form.cc_emails.filter(e => e !== email));

  const submit = async () => {
    if (submitting) return;
    if (!form.subject || !form.description) { setError("Subject and description are required"); return; }
    if (form.scheduled_at && new Date(form.scheduled_at).getTime() <= Date.now()) { setError("Scheduled time must be in the future."); return; }
    // An address typed into CC but never "Added" used to be dropped without a word.
    const pendingCc = ccInput.trim().toLowerCase().replace(/[,;]+$/, "");
    if (pendingCc && !isEmailAddr(pendingCc)) { setCcError("Fix or clear the CC address before submitting."); return; }
    const ccList = pendingCc && !form.cc_emails.includes(pendingCc) ? [...form.cc_emails, pendingCc] : form.cc_emails;
    if (pendingCc) { set("cc_emails", ccList); setCcInput(""); }
    setSubmitting(true); setError("");
    try {
      // attachments_pending tells the server files follow, so the first-touch AI
      // waits for them instead of answering before the screenshot is there.
      const payload = {
        ...form,
        cc_emails: ccList,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : undefined,
        attachments_pending: files.length > 0 ? true : undefined,
        device_id: device?.id || undefined,
      };
      const res = await createPortalTicket(payload);
      // The ticket id names the storage folder, so files go up right after creation.
      let attached = 0, uploadError = "";
      if (files.length) {
        try {
          setPct(0);
          const up = await uploadPortalAttachments(res.data.id, files, { onUploadProgress: (p) => { if (p.total) setPct(Math.round((p.loaded / p.total) * 100)); } });
          const blocked = up.data?.quarantined?.length || 0;
          attached = files.length - blocked;
          if (blocked) uploadError = `${blocked} file${blocked > 1 ? "s were" : " was"} blocked by the malware scanner and not attached.`;
        } catch (err) {
          uploadError = `Your ticket was created, but the files didn't upload: ${sendFailure(err, "The files").replace(/ — your message and files are still here\.$/, ".")} Open the ticket and attach them to a reply.`;
        }
        setPct(null);
      }
      try { sessionStorage.removeItem("midtc_portal_newticket"); } catch { /* ignore */ }
      // Confirm the submission instead of silently jumping to the ticket — the
      // submitter needs to see that it went in, and their ticket number.
      setCreated({
        id:            res.data.id,
        ticket_number: res.data.ticket_number,
        subject:       form.subject,
        priority:      form.priority,
        scheduled:     !!res.data.scheduled,
        cc_emails:     ccList,
        attached,
        uploadError,
      });
    } catch (err) {
      setError(err.response?.status === 401
        ? "You were signed out, so the ticket wasn't sent. Sign in again and press Submit — what you typed and attached is still here."
        : !err.response ? "The connection dropped, so the ticket wasn't sent. Check your internet and press Submit again — nothing you typed was lost."
        : err.response?.data?.error || "Failed to submit ticket");
    }
    setSubmitting(false);
  };

  const selectStyle = { width:"100%", background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, outline:"none" };
  const labelStyle  = { display:"block", fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.45)", marginBottom:6 };

  return (
    <div style={{ maxWidth:640 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
        <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", padding:0, fontSize:13 }}>←</button>
        <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"#fff" }}>Submit a Ticket</h2>
      </div>

      {/* Ticket Submitted confirmation */}
      {created && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:20 }}>
          <div style={{ background:"#0f172a", border:"1px solid rgba(52,211,153,0.35)", borderRadius:16, padding:30, width:"100%", maxWidth:440, textAlign:"center", boxShadow:"0 20px 60px rgba(0,0,0,0.6)" }}>
            <div style={{ width:56, height:56, borderRadius:"50%", margin:"0 auto 16px", background:"rgba(52,211,153,0.14)", border:"1px solid rgba(52,211,153,0.4)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <CheckCircle size={28} color="#34d399" />
            </div>

            <h2 style={{ fontSize:18, fontWeight:800, color:"#fff", margin:"0 0 6px" }}>
              {created.scheduled ? "Ticket Scheduled" : "Ticket Submitted"}
            </h2>
            <p style={{ fontSize:13, color:"rgba(255,255,255,0.55)", margin:"0 0 18px", lineHeight:1.55 }}>
              {created.scheduled
                ? "Your ticket is held and will be sent to our team at the date and time you chose."
                : "Your ticket has been received and our team has been notified. A confirmation email is on its way."}
            </p>

            <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10, padding:"12px 14px", textAlign:"left", display:"flex", flexDirection:"column", gap:7, marginBottom:20 }}>
              {[
                ["Ticket",   created.ticket_number || "—",     true],
                ["Subject",  created.subject,                  false],
                ["Priority", created.priority,                 false],
                ["CC",       created.cc_emails?.join(", "),    false],
                ["Attached", created.attached ? `${created.attached} file${created.attached > 1 ? "s" : ""}` : "", false],
              ].filter(([, v]) => v).map(([label, value, strong]) => (
                <div key={label} style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:700, flexShrink:0 }}>{label}</span>
                  <span style={{ fontSize: strong ? 13 : 12, fontWeight: strong ? 800 : 400, color: strong ? primary : "rgba(255,255,255,0.8)", textAlign:"right", wordBreak:"break-word" }}>{value}</span>
                </div>
              ))}
            </div>

            {created.uploadError && (
              <div style={{ display:"flex", alignItems:"flex-start", gap:8, textAlign:"left", background:"rgba(251,191,36,0.1)", border:"1px solid rgba(251,191,36,0.35)", borderRadius:8, padding:"10px 12px", marginBottom:16, fontSize:12, color:"#fcd34d" }}>
                <AlertCircle size={14} style={{ flexShrink:0, marginTop:1 }} /> {created.uploadError}
              </div>
            )}

            <div style={{ display:"flex", gap:10 }}>
              <button
                onClick={() => navigate(`${base}/tickets/${created.id}`)}
                style={{ flex:1, background:primary, color:"#fff", border:"none", borderRadius:8, padding:"10px", fontSize:13, fontWeight:700, cursor:"pointer" }}
              >
                View Ticket
              </button>
              <button
                onClick={() => navigate(`${base}/tickets`)}
                style={{ flex:1, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", color:"rgba(255,255,255,0.6)", padding:"10px", borderRadius:8, fontSize:13, cursor:"pointer" }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={{ background:"#7f1d1d", color:"#fca5a5", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:20, display:"flex", alignItems:"center", gap:8 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:14, padding:28, border:"1px solid rgba(255,255,255,0.08)", display:"flex", flexDirection:"column", gap:18 }}>
        <div>
          <label style={labelStyle}>Subject *</label>
          <input value={form.subject} onChange={e => set("subject", e.target.value)} placeholder="Brief description of the issue"
            style={{ ...selectStyle, padding:"10px 14px" }} />
          {device && (
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, marginTop:8, padding:"4px 10px", borderRadius:99, fontSize:12,
              color:"rgba(255,255,255,0.8)", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.14)" }}>
              <Laptop size={13} /> About this device: <strong>{device.name}</strong>
              <button type="button" aria-label="Don't link a device" onClick={() => setDevice(null)}
                style={{ background:"none", border:"none", color:"rgba(255,255,255,0.45)", cursor:"pointer", padding:0, display:"flex" }}><X size={13} /></button>
            </div>
          )}
        </div>
        <div>
          <label style={labelStyle}>Description *</label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)} onPaste={onPaste}
            placeholder="Please describe the issue in detail — what happened, when, and any steps to reproduce. You can paste screenshots straight in." rows={5}
            style={{ ...selectStyle, resize:"vertical", lineHeight:1.6 }} />
          <PendingAttachments files={files} onAdd={addFiles} onRemove={removeFile} disabled={submitting} />
          <UploadProgress pct={pct} label="Uploading your files" />
          {fileNotice && (
            <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:8, fontSize:12, color:"#fcd34d" }}>
              <AlertCircle size={13} /> {fileNotice}
            </div>
          )}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <div>
            <label style={labelStyle}>Category</label>
            <select value={form.category} onChange={e => set("category", e.target.value)} style={selectStyle}>
              {["General","Hardware","Software","Network","Email","Security","Other"].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Priority</label>
            <select value={form.priority} onChange={e => set("priority", e.target.value)} style={selectStyle}>
              {["Low","Medium","High","Critical"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={labelStyle}>Schedule for later (optional)</label>
          <input type="datetime-local" value={form.scheduled_at} onChange={e => set("scheduled_at", e.target.value)} style={selectStyle} />
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:6 }}>
            Leave blank to submit now. If set, your ticket is held and automatically sent to our team at that date &amp; time.
          </div>
        </div>
        <div>
          <label style={labelStyle}>CC (optional)</label>
          <div style={{ display:"flex", gap:8 }}>
            <input
              value={ccInput}
              onChange={e => { setCcInput(e.target.value); setCcError(""); }}
              onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addCc(); } }}
              aria-label="Add someone to CC"
              placeholder="colleague@company.com — they'll get the updates too"
              style={{ ...selectStyle, flex:1, padding:"9px 14px" }}
            />
            <button type="button" onClick={addCc} style={{ padding:"9px 16px", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, color:"rgba(255,255,255,0.6)", fontSize:13, cursor:"pointer", whiteSpace:"nowrap" }}>
              Add
            </button>
          </div>
          {ccError && <div role="alert" style={{ fontSize:12, color:"#fcd34d", marginTop:6 }}>{ccError}</div>}
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:6 }}>
            People you copy get the email updates. Colleagues at {branding.company_name || "your company"} can also see the ticket here and reply.
          </div>
          {form.cc_emails.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
              {form.cc_emails.map(e => (
                <span key={e} style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:"rgba(255,255,255,0.65)", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:99, padding:"3px 10px" }}>
                  {e}
                  <button type="button" aria-label={`Remove ${e}`} onClick={() => removeCc(e)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.35)", cursor:"pointer", padding:0, lineHeight:1, fontSize:14 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10, paddingTop:4 }}>
          <button onClick={() => navigate(-1)} style={{ padding:"9px 20px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, color:"rgba(255,255,255,0.5)", fontSize:13, cursor:"pointer" }}>
            Cancel
          </button>
          <button onClick={submit} disabled={submitting} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 24px", background:primary, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:600, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.7 : 1 }}>
            <Send size={13} /> {submitting ? (pct != null ? "Uploading files…" : "Submitting…") : "Submit Ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  PORTAL DEVICES  (client_admin only)
// =============================================================
function PortalDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPortalDevices().then(r => { setDevices(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const scoreColor = (s) => s >= 80 ? "#22c55e" : s >= 50 ? "#eab308" : "#ef4444";

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"#fff" }}>Devices</h2>
        <button onClick={() => { setLoading(true); getPortalDevices().then(r => { setDevices(r.data); setLoading(false); }); }} style={{ background:"none", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"7px 14px", color:"rgba(255,255,255,0.5)", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:12 }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:48, color:"rgba(255,255,255,0.3)" }}>Loading devices...</div>
      ) : devices.length === 0 ? (
        <div style={{ textAlign:"center", padding:48, color:"rgba(255,255,255,0.3)" }}>
          <Monitor size={40} style={{ marginBottom:12, opacity:0.3 }} />
          <div>No devices found</div>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:16 }}>
          {devices.map(d => {
            const score = d.health_score ?? 100;
            const alerts = typeof d.alerts === "string" ? JSON.parse(d.alerts || "[]") : (d.alerts || []);
            return (
              <div key={d.id} style={{ background:"rgba(255,255,255,0.05)", borderRadius:12, padding:20, border:"1px solid rgba(255,255,255,0.08)" }}>
                {/* Device name + score */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:"#fff" }}>{d.device_name}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:2 }}>{d.os} {d.os_version}</div>
                  </div>
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontSize:22, fontWeight:800, color:scoreColor(score) }}>{score}</div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>Health</div>
                  </div>
                </div>

                {/* Metrics */}
                {[
                  { label:"CPU",  val:d.cpu_load_percent },
                  { label:"RAM",  val:d.ram_used_percent },
                  { label:"Disk", val:d.disk_used_percent },
                ].map(({ label, val }) => val != null && (
                  <div key={label} style={{ marginBottom:8 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:3 }}>
                      <span>{label}</span><span>{Math.round(val)}%</span>
                    </div>
                    <div style={{ height:5, background:"rgba(255,255,255,0.08)", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${val}%`, background: val>=90?"#ef4444":val>=75?"#eab308":"#22c55e", borderRadius:3 }} />
                    </div>
                  </div>
                ))}

                {/* Alerts */}
                {alerts.length > 0 && (
                  <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:4 }}>
                    {alerts.map((a, i) => (
                      <div key={i} style={{ fontSize:11, color:"#fcd34d", background:"rgba(253,186,116,0.08)", padding:"4px 8px", borderRadius:4, display:"flex", alignItems:"center", gap:6 }}>
                        <AlertCircle size={10} /> {a}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop:10, fontSize:11, color:"rgba(255,255,255,0.2)" }}>
                  Last seen: {timeAgo(d.checked_at || d.last_seen)}
                  {d.logged_in_user && ` · ${d.logged_in_user}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================
// =============================================================
//  PORTAL — SECURE LINK VIEWER
// =============================================================
function SecureLinkPortalView({ branding, contact, onLogout }) {
  const { shareToken } = useParams();
  const primary = branding?.primary_color || "#1d4ed8";
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [denied,  setDenied]  = useState(null); // { recipient_email, your_email } on wrong-account

  useEffect(() => {
    getPortalSecureLink(shareToken)
      .then(r => setData(r.data))
      .catch(e => {
        const d = e?.response?.data;
        if (d?.code === "wrong_account") setDenied({ recipient_email: d.recipient_email, your_email: d.your_email || contact?.email });
        else setError(d?.error || "Unable to load secure link.");
      })
      .finally(() => setLoading(false));
  }, [shareToken]);

  // Switch account: drop the portal session, then re-auth as the intended recipient.
  // SSO clients go straight to the Microsoft chooser (login_hint + select_account);
  // password clients land on the prefilled login form at this same secure URL.
  const switchAccount = () => {
    if (onLogout) onLogout();
    if (branding?.sso_enabled) {
      const hint = denied?.recipient_email ? `&login_hint=${encodeURIComponent(denied.recipient_email)}` : "";
      window.location.href = `${API_URL}/api/auth/m365/portal/login?return=${encodeURIComponent(window.location.pathname)}${hint}`;
    }
    // else: onLogout() re-renders PortalLogin (prefilled with the recipient) at this URL
  };

  if (loading) return (
    <div style={{ padding:48, textAlign:"center", color:"#64748b", fontSize:14 }}>
      <div style={{ width:32, height:32, border:`3px solid ${primary}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite", margin:"0 auto 12px" }} />
      Loading secure content…
    </div>
  );

  if (denied) return (
    <div style={{ padding:32, maxWidth:520 }}>
      <div style={{ background:"#1e293b", border:"1px solid #7f1d1d", borderRadius:12, padding:28, textAlign:"center" }}>
        <div style={{ fontSize:36, marginBottom:12 }}>🔒</div>
        <div style={{ fontSize:16, fontWeight:700, color:"#f1f5f9", marginBottom:8 }}>Wrong account</div>
        <div style={{ fontSize:14, color:"#94a3b8", lineHeight:1.6 }}>
          This secure link was sent to <strong style={{ color:"#e2e8f0" }}>{denied.recipient_email}</strong>.<br/>
          You're signed in as <strong style={{ color:"#e2e8f0" }}>{denied.your_email}</strong>.
        </div>
        <button onClick={switchAccount} style={{ marginTop:20, width:"100%", padding:"12px", background:primary, border:"none", borderRadius:9, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer" }}>
          Sign in as {denied.recipient_email}
        </button>
        <div style={{ marginTop:14, fontSize:12, color:"#64748b", lineHeight:1.5 }}>
          If you can't sign in as {denied.recipient_email}, you don't have access to this content. Contact MIDTC Support if you believe this is an error.
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ padding:32, maxWidth:520 }}>
      <div style={{ background:"#1e293b", border:"1px solid #7f1d1d", borderRadius:12, padding:28, textAlign:"center" }}>
        <div style={{ fontSize:36, marginBottom:12 }}>🔒</div>
        <div style={{ fontSize:16, fontWeight:700, color:"#f1f5f9", marginBottom:8 }}>Access Denied</div>
        <div style={{ fontSize:14, color:"#94a3b8", lineHeight:1.6 }}>{error}</div>
      </div>
    </div>
  );

  if (!data) return null;

  const files = data.files || [];
  const baseUrl = process.env.REACT_APP_API_URL || "";

  return (
    <div style={{ padding:24, maxWidth:680 }}>
      {/* Header card */}
      <div style={{ background:"#1e293b", border:`1px solid rgba(59,130,246,0.25)`, borderRadius:12, overflow:"hidden", marginBottom:16 }}>
        <div style={{ background:`linear-gradient(135deg, ${primary}22, ${primary}0a)`, borderBottom:"1px solid rgba(59,130,246,0.15)", padding:"16px 20px", display:"flex", alignItems:"center", gap:10 }}>
          <Lock size={18} color={primary} />
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:"#f1f5f9" }}>{data.title || "Secure Content"}</div>
            <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>
              Sent securely to <strong style={{ color:"#94a3b8" }}>{contact?.email}</strong>
              {data.remaining !== null && ` · ${data.remaining} view${data.remaining !== 1 ? "s" : ""} remaining`}
            </div>
          </div>
        </div>

        {/* Message */}
        {data.message && (
          <div style={{ padding:"16px 20px", borderBottom: files.length ? "1px solid #1e293b" : "none" }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:"#475569", marginBottom:8 }}>Message</div>
            <div style={{ fontSize:14, color:"#cbd5e1", lineHeight:1.7, whiteSpace:"pre-wrap" }}>{data.message}</div>
          </div>
        )}

        {/* Files */}
        {files.length > 0 && (
          <div style={{ padding:"16px 20px" }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:"#475569", marginBottom:10 }}>
              Attachments ({files.length})
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {files.map((f, i) => {
                const isImg = f.mimetype?.startsWith("image/");
                const isPdf = f.mimetype === "application/pdf";
                const icon  = isImg ? "🖼️" : isPdf ? "📄" : "📁";
                const size  = f.size > 1048576 ? `${(f.size/1048576).toFixed(1)} MB` : f.size > 1024 ? `${(f.size/1024).toFixed(0)} KB` : `${f.size} B`;
                return (
                  <a key={i} href={`${baseUrl}${f.url}`} download={f.name} target="_blank" rel="noreferrer"
                    style={{ display:"flex", alignItems:"center", gap:12, background:"#0f172a", border:"1px solid #334155", borderRadius:8, padding:"12px 14px", textDecoration:"none", color:"#f1f5f9", transition:"border-color .15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor="#3b82f6"}
                    onMouseLeave={e => e.currentTarget.style.borderColor="#334155"}>
                    <span style={{ fontSize:20, flexShrink:0 }}>{icon}</span>
                    <span style={{ flex:1, fontSize:13, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                    <span style={{ fontSize:11, color:"#64748b", flexShrink:0 }}>{size}</span>
                    <span style={{ background:primary, color:"#fff", fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:6, flexShrink:0 }}>↓ Download</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize:11, color:"#334155", textAlign:"center", lineHeight:1.6 }}>
        This secure content was shared with you by MIDTC Support. Do not forward this link to others.
      </div>
    </div>
  );
}

//  PORTAL APP  (root — handles slug, auth, routing)
// =============================================================
// =============================================================
//  PORTAL — KNOWLEDGE BASE
// =============================================================
function PortalKnowledgeBase({ branding }) {
  const primary = branding?.primary_color || "#4f6fff";
  const [articles,   setArticles]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [kbParams] = useSearchParams();
  const [search,     setSearch]     = useState(() => kbParams.get("q") || "");
  const [filterCat,  setFilterCat]  = useState("");
  const [selected,   setSelected]   = useState(null);
  const [artLoading, setArtLoading] = useState(false);

  useEffect(() => {
    Promise.all([getPortalKBCategories(), getPortalKBArticles()])
      .then(([catRes, artRes]) => { setCategories(catRes.data); setArticles(artRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = articles.filter(a => {
    if (filterCat && String(a.category_id) !== String(filterCat)) return false;
    if (search && !a.title.toLowerCase().includes(search.toLowerCase()) &&
        !(a.excerpt || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleSelect = async (id) => {
    setArtLoading(true);
    try { const r = await getPortalKBArticle(id); setSelected(r.data); }
    catch {}
    finally { setArtLoading(false); }
  };
  useEffect(() => {
    const id = kbParams.get("article");
    if (id) handleSelect(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "";

  if (selected) {
    return (
      <div style={{ padding:24, maxWidth:860 }}>
        <button
          onClick={() => setSelected(null)}
          style={{ background:"none", border:"none", color:"#64748b", cursor:"pointer", display:"flex", alignItems:"center", gap:4, fontSize:13, marginBottom:16 }}
        >
          <ArrowLeft size={14} /> Back to Knowledge Base
        </button>
        <div style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:10, padding:28 }}>
          <div style={{ marginBottom:10 }}>
            {selected.category_name && (
              <span style={{ background:selected.category_color||"#1e3a5f", color:"#93c5fd", fontSize:11, padding:"2px 8px", borderRadius:20, marginRight:8 }}>
                {selected.category_name}
              </span>
            )}
            {selected.client_id
              ? <span style={{ fontSize:11, color:"#64748b", display:"inline-flex", alignItems:"center", gap:3 }}><Lock size={9}/> Private</span>
              : <span style={{ fontSize:11, color:"#3b82f6", display:"inline-flex", alignItems:"center", gap:3 }}><Globe size={9}/> Public</span>
            }
          </div>
          <h1 style={{ fontSize:20, fontWeight:700, color:"#f1f5f9", marginBottom:6 }}>{selected.title}</h1>
          <p style={{ fontSize:12, color:"#475569", marginBottom:20 }}>Updated {fmtDate(selected.updated_at)} · {selected.view_count} view{selected.view_count !== 1 ? "s" : ""}</p>
          <div
            style={{ color:"#cbd5e1", lineHeight:1.8, fontSize:14 }}
            dangerouslySetInnerHTML={{ __html: selected.content }}
          />
          {selected.tags?.length > 0 && (
            <div style={{ marginTop:20, display:"flex", gap:6, flexWrap:"wrap" }}>
              {selected.tags.map(t => (
                <span key={t} style={{ background:"#1e293b", color:"#64748b", fontSize:11, padding:"2px 10px", borderRadius:20 }}>#{t}</span>
              ))}
            </div>
          )}
        </div>
        <style>{`
          .portal-kb h1{font-size:20px;font-weight:700;color:#f1f5f9;margin:20px 0 8px}
          .portal-kb h2{font-size:17px;font-weight:600;color:#e2e8f0;margin:16px 0 6px}
          .portal-kb p{margin:8px 0}
          .portal-kb ul,.portal-kb ol{padding-left:20px;margin:8px 0}
          .portal-kb pre{background:#1e293b;padding:12px;border-radius:6px;font-size:13px;color:#86efac}
          .portal-kb a{color:#60a5fa}
          .portal-kb img{max-width:100%;border-radius:6px}
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ padding:24, maxWidth:1000 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
        <BookOpen size={20} style={{ color:primary }} />
        <h1 style={{ fontSize:20, fontWeight:700, color:"#f1f5f9" }}>Knowledge Base</h1>
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:20 }}>
        <div style={{ position:"relative", flex:1 }}>
          <Search size={14} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#475569" }} />
          <input
            style={{ background:"#1e293b", border:"1px solid #334155", borderRadius:8, padding:"8px 12px 8px 32px", color:"#f1f5f9", fontSize:13, width:"100%", boxSizing:"border-box" }}
            placeholder="Search articles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          style={{ background:"#1e293b", border:"1px solid #334155", borderRadius:8, padding:"8px 12px", color:"#f1f5f9", fontSize:13 }}
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ display:"flex", justifyContent:"center", padding:40 }}>
          <div style={{ width:24, height:24, border:`3px solid ${primary}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:48, color:"#475569" }}>
          <BookOpen size={36} style={{ marginBottom:10, opacity:0.3 }} />
          <p>No articles found</p>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 }}>
          {filtered.map(a => (
            <div
              key={a.id}
              onClick={() => handleSelect(a.id)}
              style={{ background:"#0f172a", border:"1px solid #1e293b", borderRadius:10, padding:16, cursor:"pointer", transition:"border-color .15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor="#334155"}
              onMouseLeave={e => e.currentTarget.style.borderColor="#1e293b"}
            >
              {a.category_name && (
                <span style={{ background:a.category_color||"#1e3a5f", color:"#93c5fd", fontSize:10, padding:"2px 7px", borderRadius:20, display:"inline-block", marginBottom:8 }}>
                  {a.category_name}
                </span>
              )}
              <h3 style={{ fontSize:13, fontWeight:600, color:"#f1f5f9", marginBottom:6, lineHeight:1.4 }}>{a.title}</h3>
              {a.excerpt && (
                <p style={{ fontSize:12, color:"#64748b", lineHeight:1.5, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                  {a.excerpt.replace(/<[^>]+>/g, "")}
                </p>
              )}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10, borderTop:"1px solid #1e293b", paddingTop:8 }}>
                <span style={{ fontSize:11, color:"#475569" }}>{fmtDate(a.updated_at)}</span>
                <span style={{ fontSize:11, color:"#475569", display:"flex", alignItems:"center", gap:3 }}><Eye size={10}/>{a.view_count}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================
//  DESKTOP APP DOWNLOAD NUDGE
//  Shown after login when the contact hasn't yet registered a device from the
//  desktop app. Signing into the desktop app auto-assigns their device to them
//  and their client (backend /api/devices/self-register), so this is what closes
//  the "unassigned device" gap. Dismissible per session; returns next login until
//  they've actually registered.
// =============================================================
function detectOS() {
  const ua = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
  if (/mac|iphone|ipad|ipod/.test(ua)) return "mac";
  if (/win/.test(ua)) return "windows";
  return "other";
}

function DesktopDownloadModal({ branding }) {
  const [show, setShow] = useState(false);
  const primary = branding?.primary_color || "#4f6fff";
  const os = detectOS();
  // Phones/tablets can't run the desktop installer — skip the nudge entirely
  // (it would also mis-detect iPhones as "mac" and push the macOS build).
  const isMobileDevice = /android|iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    if (isMobileDevice) return;
    // Show at most once per 24h (persisted across sessions/tabs), and ONLY to users
    // who haven't already installed + signed into the desktop app (the backend
    // reports registered=true once a device is linked to this contact).
    const DAY = 24 * 60 * 60 * 1000;
    const last = parseInt(localStorage.getItem("midtc_desktop_nudge_last_shown") || "0", 10);
    if (Date.now() - last < DAY) return;
    getPortalDesktopStatus()
      .then(r => {
        if (!r.data?.registered) {
          // Stamp now so it won't reappear today even if the user ignores it.
          localStorage.setItem("midtc_desktop_nudge_last_shown", String(Date.now()));
          setShow(true);
        }
      })
      .catch(() => {});
  }, [isMobileDevice]);

  if (!show) return null;

  const dismiss = () => setShow(false);

  const DownloadBtn = ({ platform, label, sub, icon, primaryCta }) => (
    <a
      href={`${API_URL}/downloads/${platform}`}
      style={{
        display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:10,
        textDecoration:"none", flex:1, minWidth:200,
        background: primaryCta ? primary : "rgba(255,255,255,0.06)",
        border: `1px solid ${primaryCta ? primary : "rgba(255,255,255,0.12)"}`,
      }}
    >
      <div style={{ width:38, height:38, borderRadius:8, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
        background: primaryCta ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)" }}>
        {icon}
      </div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{label}</div>
        <div style={{ fontSize:11, color: primaryCta ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.5)" }}>{sub}</div>
      </div>
      <Download size={15} color={primaryCta ? "#fff" : "rgba(255,255,255,0.5)"} style={{ marginLeft:"auto", flexShrink:0 }} />
    </a>
  );

  const winBtn = <DownloadBtn key="win" platform="windows" label="Download for Windows" sub="Windows 10 or later · 64-bit" icon={<MonitorDown size={20} color="#fff" />} primaryCta={os === "windows"} />;
  const macBtn = <DownloadBtn key="mac" platform="mac"     label="Download for macOS"   sub="macOS 10.15 or later"        icon={<Apple size={20} color="#fff" />}        primaryCta={os === "mac"} />;
  // Put the user's detected platform first.
  const buttons = os === "mac" ? [macBtn, winBtn] : [winBtn, macBtn];

  return (
    <div
      onClick={dismiss}
      style={{ position:"fixed", inset:0, background:"rgba(2,6,23,0.72)", backdropFilter:"blur(3px)",
        display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background:"#111b2f", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16,
          maxWidth:560, width:"100%", padding:"32px 32px 28px", position:"relative",
          boxShadow:"0 24px 60px rgba(0,0,0,0.5)" }}
      >
        <button onClick={dismiss} style={{ position:"absolute", top:16, right:16, background:"none", border:"none",
          color:"rgba(255,255,255,0.4)", cursor:"pointer", padding:4 }}>
          <X size={18} />
        </button>

        <div style={{ width:52, height:52, borderRadius:12, background:`${primary}22`, border:`1px solid ${primary}55`,
          display:"flex", alignItems:"center", justifyContent:"center", marginBottom:18 }}>
          <Monitor size={26} color={primary} />
        </div>

        <h2 style={{ margin:"0 0 8px", fontSize:20, fontWeight:800, color:"#fff" }}>
          Finish setup — install the desktop app
        </h2>
        <p style={{ margin:"0 0 22px", fontSize:14, color:"rgba(255,255,255,0.55)", lineHeight:1.6 }}>
          We don't see the MIDTC desktop app on your computer yet. Download it and sign in with this
          same account — that links your device to your support team so we can help you faster and keep it protected.
        </p>

        <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:18 }}>
          {buttons}
        </div>

        <button onClick={dismiss} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)",
          fontSize:12, cursor:"pointer", padding:0 }}>
          I'll do this later
        </button>
      </div>
    </div>
  );
}

export default function PortalApp() {
  const { identifier } = useParams();
  const [branding,  setBranding]  = useState(null);
  const [contact,   setContact]   = useState(null);
  const [loadingB,  setLoadingB]  = useState(true);
  const [notFound,  setNotFound]  = useState(false);

  // Load branding from slug
  useEffect(() => {
    if (!identifier) { setNotFound(true); setLoadingB(false); return; }
    getPortalBranding(identifier)
      .then(r => setBranding(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoadingB(false));
  }, [identifier]);

  // Restore session
  useEffect(() => {
    const stored = localStorage.getItem("midtc_portal_contact");
    if (stored) {
      try {
        setContact(JSON.parse(stored));
        registerMobileDevice(localStorage.getItem("midtc_portal_token")); // phones only — no-op on desktop
      } catch {}
    }
  }, []);

  // If a logged-out visitor lands on a secure-link route, fetch its recipient so the
  // login screen can say "this link is for X" and pre-target the right SSO account.
  const [secureExpected, setSecureExpected] = useState("");
  useEffect(() => {
    const m = window.location.pathname.match(/\/secure\/([^/?#]+)/);
    if (!m) return;
    getSecureLinkMeta(m[1])
      .then(r => { if (r.data?.recipient_email) setSecureExpected(r.data.recipient_email); })
      .catch(() => {});
  }, []);

  const handleLogin  = (c) => {
    setContact(c);
    registerMobileDevice(localStorage.getItem("midtc_portal_token")); // phones only — no-op on desktop
  };
  const handleLogout = () => {
    localStorage.removeItem("midtc_portal_token");
    localStorage.removeItem("midtc_portal_contact");
    setSessionExpired(false);
    setContact(null);
  };

  // Session ran out (a request came back 401, or the tab woke up after the token
  // expired): ask for the password over the current page instead of dropping the
  // person back to a login screen and losing what they had typed.
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    const check = () => {
      if (document.visibilityState !== "visible") return;
      const left = portalTokenSecondsLeft();
      if (localStorage.getItem("midtc_portal_contact") && (left === null || left <= 0)) setSessionExpired(true);
    };
    window.addEventListener(PORTAL_EXPIRED_EVENT, onExpired);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    const t = setInterval(check, 60000);
    return () => { window.removeEventListener(PORTAL_EXPIRED_EVENT, onExpired); document.removeEventListener("visibilitychange", check); window.removeEventListener("focus", check); clearInterval(t); };
  }, []);
  const handleSessionRestored = (c) => {
    setContact(c);
    setSessionExpired(false);
    try { window.dispatchEvent(new CustomEvent(SESSION_RESTORED_EVENT)); } catch { /* ignore */ }
  };

  // Loading branding
  if (loadingB) return (
    <div style={{ minHeight:"100vh", background:"#0f172a", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:36, height:36, border:"3px solid #64748b", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
    </div>
  );

  // Invalid slug
  if (notFound) return (
    <div style={{ minHeight:"100vh", background:"var(--t-page, #111b2f)", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, color:"#475569" }}>
      <X size={40} />
      <div style={{ fontSize:16 }}>Portal not found</div>
      <div style={{ fontSize:13 }}>The URL <code style={{ color:"#64748b" }}>/clientportal/{identifier}</code> doesn't match any client portal.</div>
    </div>
  );

  // Not logged in
  if (!contact) return <div className="portal-root"><PortalLogin branding={branding} onLogin={handleLogin} expectedEmail={secureExpected} /></div>;

  // Verify the logged-in contact matches this portal's client
  if (contact.clientId !== branding.client_id) {
    handleLogout();
    return <div className="portal-root"><PortalLogin branding={branding} onLogin={handleLogin} expectedEmail={secureExpected} /></div>;
  }

  // Logged in — render portal with nested routes
  return (
    <div className="portal-root">
    <PortalLayout branding={branding} contact={contact} onLogout={handleLogout}>
      {sessionExpired && <SessionExpiredModal branding={branding} contact={contact} onSignedIn={handleSessionRestored} onSignOut={handleLogout} />}
      <DesktopDownloadModal branding={branding} />
      <Routes>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<PortalDashboard branding={branding} contact={contact} />} />
        <Route path="tickets"   element={<PortalTicketList branding={branding} contact={contact} />} />
        <Route path="tickets/new" element={<PortalNewTicket branding={branding} />} />
        <Route path="tickets/:ticketId" element={<PortalTicketDetailKeyed branding={branding} contact={contact} />} />
        <Route path="knowledge-base" element={<PortalKnowledgeBase branding={branding} />} />
        <Route path="eeditions" element={<PortalEEditions branding={branding} />} />
        {contact.portalRole === "admin" && (
          <Route path="devices" element={<PortalDevices />} />
        )}
        <Route path="secure/:shareToken" element={<SecureLinkPortalView branding={branding} contact={contact} onLogout={handleLogout} />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </PortalLayout>
    </div>
  );
}
